// @ts-nocheck
import { Component } from "@theme/component";
import { formatMoney } from "@theme/money-formatting";
import { CartAddEvent, CartErrorEvent } from "@theme/events";

/**
 * Flip to `true` to skip the Gadget `submit-quiz` request and use a mock submission.
 * Use this to exercise results + add-to-cart locally without the API.
 * Set to `false` before relying on real persistence / `_skin_quiz_submission_id`.
 */
const SKIN_QUIZ_MOCK_SUBMISSION_API = false;

/** @type {Set<string>} */
const skinQuizDevWarnedKeys = new Set();

/**
 * Theme-editor / explicit debug flag only — keeps production consoles clean.
 * Append `?skin_quiz_debug=1` to the storefront URL to enable outside the theme editor.
 * @returns {boolean}
 */
function isSkinQuizDevEnvironment() {
  if (typeof window === "undefined") return false;
  try {
    if (window.Shopify?.designMode) return true;
  } catch {
    /* ignore */
  }
  const q = window.location?.search ?? "";
  return /(?:^|[?&])skin_quiz_debug=1(?:&|$)/.test(q);
}

/**
 * @param {string} code
 * @param {string} message
 * @param {unknown} [detail]
 */
function skinQuizDevWarn(code, message, detail) {
  if (!isSkinQuizDevEnvironment()) return;
  const key = `${code}::${message}::${detail == null ? "" : String(detail)}`;
  if (skinQuizDevWarnedKeys.has(key)) return;
  skinQuizDevWarnedKeys.add(key);
  console.warn(`[skin-quiz] ${message}`, detail !== undefined ? detail : "");
}

/**
 * Theme-editor / `?skin_quiz_debug=1` only — narrative debug (not deduped like dev warnings).
 * @param {string} message
 * @param {unknown} [detail]
 */
function skinQuizDebug(message, detail) {
  if (!isSkinQuizDevEnvironment()) return;
  if (detail !== undefined) {
    console.debug(`[skin-quiz] ${message}`, detail);
  } else {
    console.debug(`[skin-quiz] ${message}`);
  }
}

/**
 * @param {string} value
 * @returns {string}
 */
function normalizeQuizEmailInput(value) {
  return toText(value).trim();
}

/**
 * Lightweight format check (not full RFC); blocks empty / whitespace-only / obvious typos.
 * @param {string} value
 * @returns {boolean}
 */
function isPlausibleEmail(value) {
  const v = normalizeQuizEmailInput(value);
  if (!v) return false;
  return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(v);
}

/**
 * Resolves quiz image URL from legacy/admin shapes (object with src, string URL, or imageUrl).
 * @param {{ image?: unknown, imageUrl?: unknown } | null | undefined} root
 * @returns {string}
 */
function resolveQuizImageSrc(root) {
  if (!root || typeof root !== "object") return "";
  const img = /** @type {{ src?: unknown }} */ (root).image ?? null;
  if (typeof img === "string") return toText(img);
  if (img && typeof img === "object" && "src" in img) {
    return toText(String(/** @type {{ src?: unknown }} */ (img).src ?? ""));
  }
  return toText(root.imageUrl ?? "");
}

/**
 * @param {string} typeRaw
 */
function isMultiselectQuestionType(typeRaw) {
  const t = toText(typeRaw).toLowerCase();
  return t === "multi" || t === "multiple";
}

/**
 * @param {unknown} id
 * @returns {boolean}
 */
function isValidBundleVariantId(id) {
  const n = Number(id);
  return Number.isFinite(n) && n > 0;
}

/**
 * Bundle add: first available variant in `product.variants` order, else first with id if data incomplete,
 * else Liquid `data-variant-id` / `data-available`. Skips when every variant is explicitly unavailable.
 * @param {HTMLElement | null | undefined} detailEl
 * @returns {{ ok: true, variantId: string, available: boolean, pick: string } | { ok: false, reason: 'no_registry' | 'unavailable' | 'invalid_variant', variantId?: string }}
 */
function resolveQuizBundleVariant(detailEl) {
  if (!(detailEl instanceof HTMLElement)) {
    return { ok: false, reason: "no_registry" };
  }

  const raw = detailEl.dataset.bundleVariants?.trim();
  if (raw) {
    try {
      const list = JSON.parse(raw);
      if (Array.isArray(list) && list.length > 0) {
        const withIds = list.filter(
          (x) => x && x.id != null && isValidBundleVariantId(x.id),
        );
        if (withIds.length > 0) {
          const firstAvail = withIds.find((x) => x.available === true);
          if (firstAvail) {
            return {
              ok: true,
              variantId: String(Number(firstAvail.id)),
              available: true,
              pick: "first_available",
            };
          }
          const allExplicitlyUnavailable =
            withIds.length > 0 &&
            withIds.every((x) => x.available === false);
          if (allExplicitlyUnavailable) {
            skinQuizDebug("bundle-variant-all-unavailable", {
              productKey: detailEl.dataset.productKey,
              handle: detailEl.dataset.productHandle,
            });
            return { ok: false, reason: "unavailable" };
          }
          const first = withIds[0];
          skinQuizDebug("bundle-variant-fallback-first", {
            productKey: detailEl.dataset.productKey,
            variantId: String(first.id),
            note: "incomplete or mixed availability flags",
          });
          return {
            ok: true,
            variantId: String(Number(first.id)),
            available: first.available !== false,
            pick: "first_in_list_fallback",
          };
        }
      }
    } catch {
      skinQuizDebug("bundle-variant-json-parse-failed", {
        productKey: detailEl.dataset.productKey,
      });
    }
  }

  const variantIdRaw = (detailEl.dataset.variantId ?? "").trim();
  const variantIdNum = Number(variantIdRaw);
  if (
    !variantIdRaw ||
    !Number.isFinite(variantIdNum) ||
    variantIdNum <= 0
  ) {
    return { ok: false, reason: "invalid_variant" };
  }
  const available = detailEl.dataset.available === "true";
  return {
    ok: true,
    variantId: String(variantIdNum),
    available,
    pick: "liquid_placeholder_variant",
  };
}

/**
 * Shared rules for bundle CTA enablement and sequential bundle add (one source of truth).
 * @param {HTMLElement | null | undefined} detailEl `[data-quiz-product-detail]`
 * @returns {{ addable: boolean, reason: 'addable' | 'unavailable' | 'invalid_variant' | 'no_registry', resolvedVariantId?: string, pick?: string }}
 */
function classifyQuizDetailForBundle(detailEl) {
  const res = resolveQuizBundleVariant(detailEl);
  if (!res.ok) {
    return { addable: false, reason: res.reason };
  }
  if (!res.variantId) {
    return { addable: false, reason: "invalid_variant" };
  }
  if (!res.available) {
    return { addable: false, reason: "unavailable" };
  }
  return {
    addable: true,
    reason: "addable",
    resolvedVariantId: res.variantId,
    pick: res.pick,
  };
}

/**
 * @typedef {Object} SkinQuizRefs
 * @property {HTMLElement} panel
 */

/**
 * @extends {Component<SkinQuizRefs>}
 */
class SkinQuizComponent extends Component {
  requiredRefs = ["panel"];

  state = {
    stepIndex: -1,
    answers: {},
    activeResultTab: 0,
    activeProfileId: "",
    email: "",
    hasSubmitted: false,
    isSubmitting: false,
    submissionId: null,
    submittedAt: null,
  };

  connectedCallback() {
    super.connectedCallback();

    skinQuizDebug("lifecycle-connected", {
      quizId: toSafeId(this.dataset.quizId || ""),
    });

    this.#quizzes = normalizeQuizCollection(this.#getRawQuizPayload());
    this.#devAuditNormalizedQuizzes();
    this.#cacheDom();
    this.#bindEvents();
    this.#attachQuizImageErrorHandlers();

    const restored = this.#tryRestorePersistedState();
    skinQuizDebug("restore-attempt-complete", { restored });
    if (!restored) {
      this.#showStep(-1);
    }

    this.#syncResultTabs();
    this.#syncBackNav();
    this.#syncSubmitButtons();
    queueMicrotask(() => {
      this.#syncAdvanceButtons();
    });
    document.addEventListener("keydown", this.#handleKeydown);
    document.addEventListener(CartAddEvent.eventName, this.#handleCartUpdateResync);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    skinQuizDebug("lifecycle-disconnected", {
      quizId: toSafeId(this.dataset.quizId || ""),
      stepIndex: this.state.stepIndex,
    });
    this.#persistQuizState("disconnect");
    document.removeEventListener(CartAddEvent.eventName, this.#handleCartUpdateResync);
    this.removeEventListener(
      "click",
      this.#handleClick,
      SkinQuizComponent.#clickCapture,
    );
    this.removeEventListener("change", this.#handleChange);
    this.removeEventListener("input", this.#handleInput);
    this.removeEventListener("focusout", this.#handleFocusOut);
    const panel = this.refs.panel;
    if (panel instanceof HTMLElement) {
      panel.removeEventListener("click", this.#stopPanelClickBubbling);
    }
    document.removeEventListener("keydown", this.#handleKeydown);
  }

  #quizzes = [];
  #screens = [];
  #questionScreens = [];
  #resultScreen = null;
  #tabButtons = [];
  #tabPanels = [];
  #backButtons = [];
  /** @type {HTMLElement | null} */
  #submissionOverlay = null;
  /** @type {HTMLElement | null} */
  #productRegistry = null;
  #defaultProfileTitle = "";
  #defaultProfileDescription = "";
  #defaultProfileImageSrc = "";
  #defaultProfileImageAlt = "";
  #defaultProfileImageHidden = true;

  /** Ensures unique form ids when the same registry card is cloned into multiple result rows. */
  #quizFormCloneSeq = 0;

  /** Prevents overlapping routine bundle adds (double-submit / rapid tab switches). */
  #bundleCartAddLocked = false;

  /** Click delegation uses capture so it still runs when the panel stops bubbled clicks from reaching this host. */
  static #clickCapture = true;

  /** Keeps add-to-cart / form clicks from bubbling to document (e.g. header mega menu outside-click). */
  #stopPanelClickBubbling = (event) => {
    event.stopPropagation();
  };

  /**
   * Cart / section morph can replace markup inside this component without disconnecting it.
   * Checkbox nodes then disagree with `state.answers` (empty UI but Next still enabled).
   * Re-query DOM refs and re-apply state; rebuild results when on the result step.
   * @param {string} reason
   */
  #resyncDomWithQuizState(reason) {
    if (this.#quizzes.length === 0) return;
    const substantial =
      this.state.stepIndex >= 0 ||
      this.state.hasSubmitted === true ||
      Object.keys(this.state.answers ?? {}).length > 0;
    if (!substantial) return;

    this.#cacheDom();
    this.#attachQuizImageErrorHandlers();

    for (const screen of this.#questionScreens) {
      if (!(screen instanceof HTMLElement)) continue;
      const qid = toSafeId(screen.dataset.questionId ?? "");
      if (qid) this.#syncQuestionSelection(screen, qid);
    }

    const step = this.state.stepIndex;
    for (const screen of this.#screens) {
      if (!(screen instanceof HTMLElement)) continue;
      const screenStep = Number(screen.dataset.step ?? -99);
      screen.hidden = screenStep !== step;
    }

    this.#syncResultTabs();
    this.#syncBackNav();
    this.#syncSubmitButtons();

    const qCount = this.#questionScreens.length;
    if (step === qCount && qCount >= 0) {
      const computed = this.#populateComputedResults();
      if (!computed) {
        skinQuizDebug("dom-resync-populate-failed", { reason });
      }
    }

    this.#syncQuizLineItemPropertiesInRoot(this);
    queueMicrotask(() => {
      this.#syncAdvanceButtons();
    });
    skinQuizDebug("quiz-dom-resynced", { reason, stepIndex: step });
  }

  /**
   * Section morph often runs in the same turn as `cart:update` but after some listeners.
   * Run resync on a microtask and after the next paint so replaced checkbox nodes are targeted.
   * @param {string} reason
   */
  #scheduleQuizDomResync(reason) {
    const run = () => {
      this.#resyncDomWithQuizState(reason);
    };
    queueMicrotask(run);
    requestAnimationFrame(() => {
      requestAnimationFrame(run);
    });
  }

  #handleCartUpdateResync = () => {
    this.#scheduleQuizDomResync("cart-update");
  };

  #handleFocusOut = (event) => {
    const target = event.target;
    if (!(target instanceof HTMLInputElement)) return;
    if (target.name !== "quiz-email") return;
    this.#validateEmailField("blur");
  };

  /** @returns {string} */
  #getQuizPersistKey() {
    const id = toSafeId(
      this.dataset.quizId || this.#quizzes[0]?.id || "skin-quiz",
    );
    return `skinQuiz:v1:${id}`;
  }

  #clearPersistedQuizState() {
    try {
      sessionStorage.removeItem(this.#getQuizPersistKey());
    } catch {
      /* ignore */
    }
  }

  /**
   * @returns {Record<string, unknown> | null}
   */
  #readPersistedQuizPayloadParsed() {
    try {
      const raw = sessionStorage.getItem(this.#getQuizPersistKey());
      if (!raw) return null;
      const data = JSON.parse(raw);
      if (data.v !== 1) return null;
      const storedQuizId = toSafeId(String(data.quizId ?? ""));
      const currentQuizId = toSafeId(
        this.dataset.quizId || this.#quizzes[0]?.id || "",
      );
      if (!storedQuizId || storedQuizId !== currentQuizId) return null;
      return data;
    } catch {
      return null;
    }
  }

  /**
   * Idle intro: no answers, not submitted, still on step -1. A second mounted instance
   * (e.g. duplicate header embed) must not overwrite real progress in sessionStorage.
   * @param {Record<string, unknown>} payload
   */
  #isWeakIntroPersistCandidate(payload) {
    const step =
      typeof payload.stepIndex === "number" ? payload.stepIndex : -99;
    if (step !== -1) return false;
    if (payload.hasSubmitted === true) return false;
    const answers = payload.answers;
    const keys =
      answers && typeof answers === "object" && !Array.isArray(answers)
        ? Object.keys(answers)
        : [];
    return keys.length === 0;
  }

  /**
   * @param {Record<string, unknown>} payload
   */
  #hasSubstantialPersistedProgress(payload) {
    if (!payload || payload.v !== 1) return false;
    const step =
      typeof payload.stepIndex === "number" ? payload.stepIndex : -99;
    if (step >= 0) return true;
    if (payload.hasSubmitted === true) return true;
    const answers = payload.answers;
    const keys =
      answers && typeof answers === "object" && !Array.isArray(answers)
        ? Object.keys(answers)
        : [];
    return keys.length > 0;
  }

  /**
   * Persist quiz progress so section morph / header re-render after cart updates does not wipe results.
   * @param {string} [reason]
   */
  #persistQuizState(reason) {
    if (this.#quizzes.length === 0) return;
    try {
      const payload = {
        v: 1,
        quizId: toSafeId(this.dataset.quizId || this.#quizzes[0]?.id || ""),
        stepIndex: this.state.stepIndex,
        answers: this.state.answers,
        activeResultTab: this.state.activeResultTab,
        activeProfileId: this.state.activeProfileId,
        email: this.state.email,
        hasSubmitted: this.state.hasSubmitted,
        submissionId: this.state.submissionId,
        submittedAt: this.state.submittedAt,
      };
      const existing = this.#readPersistedQuizPayloadParsed();
      if (
        existing &&
        this.#isWeakIntroPersistCandidate(payload) &&
        this.#hasSubstantialPersistedProgress(existing)
      ) {
        skinQuizDebug("state-persist-skipped-weak-over-substantial", {
          reason,
          existingStep: existing.stepIndex,
        });
        return;
      }
      sessionStorage.setItem(this.#getQuizPersistKey(), JSON.stringify(payload));
      skinQuizDebug("state-persisted", { reason, stepIndex: payload.stepIndex });
    } catch {
      /* ignore quota / private mode */
    }
  }

  /**
   * @returns {boolean}
   */
  #tryRestorePersistedState() {
    const quiz = this.#quizzes[0];
    if (!quiz) {
      skinQuizDebug("state-restore-no-quiz-json", {});
      return false;
    }

    /** @type {string | null} */
    let raw = null;
    try {
      raw = sessionStorage.getItem(this.#getQuizPersistKey());
    } catch {
      skinQuizDebug("state-restore-storage-read-error", {});
      return false;
    }
    if (!raw) {
      skinQuizDebug("state-restore-empty-storage", {});
      return false;
    }

    /** @type {Record<string, unknown>} */
    let data;
    try {
      data = JSON.parse(raw);
    } catch {
      this.#clearPersistedQuizState();
      skinQuizDebug("state-restore-json-invalid", {});
      return false;
    }

    if (data.v !== 1) {
      this.#clearPersistedQuizState();
      skinQuizDebug("state-restore-bad-version", { v: data.v });
      return false;
    }

    const storedQuizId = toSafeId(String(data.quizId ?? ""));
    const currentQuizId = toSafeId(
      this.dataset.quizId || quiz.id || "",
    );
    if (!storedQuizId || storedQuizId !== currentQuizId) {
      skinQuizDebug("state-restore-quiz-id-mismatch", {
        storedQuizId,
        currentQuizId,
      });
      return false;
    }

    const qCount = this.#questionScreens.length;
    const stepIndex =
      typeof data.stepIndex === "number" && Number.isFinite(data.stepIndex)
        ? data.stepIndex
        : -99;
    if (stepIndex < -1 || stepIndex > qCount) {
      this.#clearPersistedQuizState();
      skinQuizDebug("state-restore-invalid-step", { stepIndex, qCount });
      return false;
    }

    const qIds = new Set((quiz.questions ?? []).map((q) => q.id));
    const answersRaw = data.answers;
    const answers =
      answersRaw && typeof answersRaw === "object" && !Array.isArray(answersRaw)
        ? /** @type {Record<string, string[]>} */ (answersRaw)
        : {};
    for (const key of Object.keys(answers)) {
      if (!qIds.has(toSafeId(key))) {
        this.#clearPersistedQuizState();
        skinQuizDebug("state-restore-invalid-answers", {
          key,
          knownIds: [...qIds],
        });
        return false;
      }
    }

    this.state.stepIndex = stepIndex;
    this.state.answers = { ...answers };
    this.state.activeResultTab =
      typeof data.activeResultTab === "number" &&
      Number.isFinite(data.activeResultTab)
        ? data.activeResultTab
        : 0;
    this.state.activeProfileId = toText(data.activeProfileId ?? "");
    this.state.email = toText(data.email ?? "");
    this.state.hasSubmitted = data.hasSubmitted === true;
    this.state.submissionId =
      data.submissionId === undefined || data.submissionId === null
        ? null
        : toText(String(data.submissionId));
    this.state.submittedAt =
      data.submittedAt === undefined || data.submittedAt === null
        ? null
        : toText(String(data.submittedAt));
    this.state.isSubmitting = false;

    const emailInput = this.querySelector('input[name="quiz-email"]');
    if (emailInput instanceof HTMLInputElement) {
      emailInput.value = this.state.email;
    }

    for (const screen of this.#questionScreens) {
      if (!(screen instanceof HTMLElement)) continue;
      const qid = toSafeId(screen.dataset.questionId ?? "");
      if (qid) this.#syncQuestionSelection(screen, qid);
    }

    this.#showStep(stepIndex);

    if (stepIndex === qCount && qCount >= 0) {
      const computed = this.#populateComputedResults();
      if (!computed) {
        skinQuizDebug("state-restore-populate-failed", {});
      }
    }

    this.#syncQuizLineItemPropertiesInRoot(this);
    skinQuizDebug("state-restored-from-storage", {
      stepIndex,
      hasSubmitted: this.state.hasSubmitted,
    });
    return true;
  }

  /**
   * @param {HTMLElement} screen
   * @returns {boolean}
   */
  #questionScreenAllowsAdvance(screen) {
    if (!(screen instanceof HTMLElement)) return false;
    const optional = screen.dataset.questionOptional === "true";
    const questionId = toSafeId(screen.dataset.questionId ?? "");
    if (!questionId) return false;
    const selected = this.state.answers[questionId];
    const has = Array.isArray(selected) && selected.length > 0;
    return optional || has;
  }

  /** @returns {boolean} */
  #advanceAllowedForCurrentStep() {
    const idx = this.state.stepIndex;
    if (idx < 0 || idx >= this.#questionScreens.length) return true;
    const screen = this.#questionScreens[idx];
    return this.#questionScreenAllowsAdvance(
      screen instanceof HTMLElement ? screen : null,
    );
  }

  #syncAdvanceButtons() {
    const busy = this.state.isSubmitting;
    for (const screen of this.#questionScreens) {
      if (!(screen instanceof HTMLElement)) continue;
      const allow = this.#questionScreenAllowsAdvance(screen);
      for (const btn of screen.querySelectorAll(
        '[data-action="next"], [data-action="submit-results"]',
      )) {
        if (!(btn instanceof HTMLButtonElement)) continue;
        const isSubmit = btn.dataset.action === "submit-results";
        btn.disabled = !allow || (isSubmit && busy);
        if (isSubmit) {
          btn.setAttribute("aria-busy", busy ? "true" : "false");
        }
      }
    }
  }

  /** @param {'blur'|'continue'} source @returns {boolean} */
  #validateEmailField(source) {
    const input = this.querySelector('input[name="quiz-email"]');
    const wrap = input?.closest(".skin-quiz__email-field");
    const errEl = this.querySelector("[data-skin-quiz-email-error]");

    if (!(input instanceof HTMLInputElement)) return true;

    const raw = input.value;
    this.state.email = raw;
    const normalized = normalizeQuizEmailInput(raw);

    const showError = (message) => {
      input.setAttribute("aria-invalid", "true");
      if (wrap instanceof HTMLElement) {
        wrap.classList.add("skin-quiz__email-field--invalid");
      }
      if (errEl instanceof HTMLElement) {
        errEl.textContent = message;
        errEl.hidden = false;
      }
      if (source === "submit") {
        const submitErr = this.querySelector("[data-skin-quiz-submit-error]");
        if (submitErr instanceof HTMLElement) {
          submitErr.textContent = message;
          submitErr.hidden = false;
        }
        requestAnimationFrame(() => {
          input.scrollIntoView({ block: "center", behavior: "smooth" });
          input.focus({ preventScroll: true });
        });
      }
      this.#announceToLiveRegion(message);
      skinQuizDebug("email-validation-failed", { source, message });
    };

    if (!normalized) {
      showError("Enter your email address to continue.");
      return false;
    }
    if (!isPlausibleEmail(raw)) {
      showError("Enter a valid email address.");
      return false;
    }

    this.#clearEmailErrorUi();
    input.value = normalized;
    this.state.email = normalized;
    return true;
  }

  #clearEmailErrorUi() {
    const input = this.querySelector('input[name="quiz-email"]');
    const wrap = input?.closest(".skin-quiz__email-field");
    const errEl = this.querySelector("[data-skin-quiz-email-error]");

    if (input instanceof HTMLInputElement) {
      input.setAttribute("aria-invalid", "false");
    }
    if (wrap instanceof HTMLElement) {
      wrap.classList.remove("skin-quiz__email-field--invalid");
    }
    if (errEl instanceof HTMLElement) {
      errEl.textContent = "";
      errEl.hidden = true;
    }
    const submitErr = this.querySelector("[data-skin-quiz-submit-error]");
    if (submitErr instanceof HTMLElement) {
      submitErr.textContent = "";
      submitErr.hidden = true;
    }
  }

  #clearSaveWarningUi() {
    const saveWarn = this.querySelector("[data-skin-quiz-save-warning]");
    if (saveWarn instanceof HTMLElement) {
      saveWarn.textContent = "";
      saveWarn.hidden = true;
    }
  }

  #bindEvents() {
    this.addEventListener(
      "click",
      this.#handleClick,
      SkinQuizComponent.#clickCapture,
    );
    this.addEventListener("change", this.#handleChange);
    this.addEventListener("input", this.#handleInput);
    this.addEventListener("focusout", this.#handleFocusOut);
    const panel = this.refs.panel;
    if (panel instanceof HTMLElement) {
      panel.addEventListener("click", this.#stopPanelClickBubbling);
    }
  }

  #cacheDom() {
    this.#screens = [...this.querySelectorAll("[data-screen]")];
    this.#questionScreens = [
      ...this.querySelectorAll('[data-screen="question"]'),
    ];
    this.#resultScreen = this.querySelector('[data-screen="result"]');
    this.#tabButtons = [...this.querySelectorAll('[data-action="tab"]')];
    this.#tabPanels = [...this.querySelectorAll("[data-tab-panel]")];
    this.#backButtons = [...this.querySelectorAll('[data-action="back"]')];
    this.#submissionOverlay = this.querySelector(
      "[data-skin-quiz-submission-overlay]",
    );
    this.#productRegistry = this.querySelector(
      "[data-skin-quiz-product-registry]",
    );
    this.#cacheResultDefaults();
  }

  #cacheResultDefaults() {
    const titleEl = this.querySelector("[data-skin-quiz-profile-title]");
    const descEl = this.querySelector("[data-skin-quiz-profile-description]");
    const imgEl = this.querySelector("[data-skin-quiz-profile-image]");

    this.#defaultProfileTitle = titleEl?.textContent?.trim() ?? "";
    this.#defaultProfileDescription = descEl?.textContent?.trim() ?? "";
    if (imgEl instanceof HTMLImageElement) {
      this.#defaultProfileImageSrc = imgEl.getAttribute("src") ?? "";
      this.#defaultProfileImageAlt = imgEl.getAttribute("alt") ?? "";
      this.#defaultProfileImageHidden = imgEl.hidden;
    }
  }

  #attachQuizImageErrorHandlers() {
    const panel = this.refs.panel;
    if (!(panel instanceof HTMLElement)) return;

    for (const img of panel.querySelectorAll("img.skin-quiz__image")) {
      if (!(img instanceof HTMLImageElement)) continue;
      if (img.dataset.skinQuizImgBound === "true") continue;
      img.dataset.skinQuizImgBound = "true";

      img.addEventListener(
        "error",
        () => {
          img.removeAttribute("src");
          img.removeAttribute("srcset");
          img.alt = "";
          img.hidden = true;
          const wrap = img.closest(".skin-quiz__image-wrap");
          if (wrap instanceof HTMLElement) {
            wrap.classList.add("skin-quiz__image-wrap--media-broken");
          }
          skinQuizDevWarn(
            "img-load",
            "Quiz image failed to load; image area collapsed.",
          );
        },
        { once: true },
      );
    }
  }

  /**
   * Dev / theme-editor-only: one-time warnings for schema drift (no effect on shoppers).
   */
  #devAuditNormalizedQuizzes() {
    if (!isSkinQuizDevEnvironment()) return;

    for (const quiz of this.#quizzes) {
      const questionById = new Map(
        (quiz?.questions ?? []).map((q) => [q.id, q]),
      );
      const optionsByQuestion = new Map(
        (quiz?.questions ?? []).map((q) => [
          q.id,
          new Set((q.options ?? []).map((o) => o.id)),
        ]),
      );

      for (const q of quiz?.questions ?? []) {
        if (!toText(q.image ?? "")) {
          skinQuizDevWarn(
            "q-image",
            "Question has no resolved image URL (hero area will be omitted in Liquid if configured).",
            q.id,
          );
        }
        if (!toText(q.title ?? "")) {
          skinQuizDevWarn("q-title", "Question missing title/headline.", q.id);
        }
        if (!(q.options?.length > 0)) {
          skinQuizDevWarn("q-options", "Question has no answer options.", q.id);
        }
        const rawSel = toText(q.selectionTypeRaw ?? "").toLowerCase();
        if (
          rawSel &&
          rawSel !== "single" &&
          rawSel !== "multi" &&
          rawSel !== "multiple"
        ) {
          skinQuizDevWarn(
            "q-type",
            "Unexpected question selection type in source JSON (normalized to single or multiple).",
            rawSel,
          );
        }
      }

      for (const rule of quiz?.result?.rules ?? []) {
        for (const c of rule?.conditions ?? []) {
          if (!toText(c.questionId ?? "")) {
            skinQuizDevWarn("rule-c", "Rule condition missing questionId.", "");
            continue;
          }
          if (!questionById.has(c.questionId)) {
            skinQuizDevWarn(
              "rule-qid",
              "Rule references unknown questionId (normalized).",
              c.questionId,
            );
          }
          const opts = optionsByQuestion.get(c.questionId);
          if (opts && !opts.has(c.answerId)) {
            skinQuizDevWarn(
              "rule-aid",
              "Rule references answerId not present on question (normalized).",
              `${c.questionId} → ${c.answerId}`,
            );
          }
        }
      }

      if (quiz?.products instanceof Map) {
        const keys = new Set(quiz.products.keys());
        for (const template of quiz?.routineTemplates ?? []) {
          for (const step of template?.steps ?? []) {
            const pk = toSafeId(step?.productKey ?? "");
            if (pk && !keys.has(pk)) {
              skinQuizDevWarn(
                "product-key",
                "Routine step references product key missing from products / productMappings registry.",
                pk,
              );
            }
          }
        }
      }
    }
  }

  #handleInput = (event) => {
    const target = event.target;
    if (!(target instanceof HTMLInputElement)) return;
    if (target.name !== "quiz-email") return;

    this.state.email = target.value;
    if (target.getAttribute("aria-invalid") === "true") {
      this.#clearEmailErrorUi();
    } else {
      const submitErr = this.querySelector("[data-skin-quiz-submit-error]");
      if (submitErr instanceof HTMLElement && !submitErr.hidden) {
        submitErr.textContent = "";
        submitErr.hidden = true;
      }
    }
    this.#persistQuizState("email-input");
  };

  #handleChange = (event) => {
    const target = event.target;
    if (!(target instanceof HTMLInputElement)) return;
    if (target.name !== "quiz-option") return;

    const questionId = toSafeId(target.dataset.questionId ?? "");
    const answerId = toSafeId(target.dataset.answerId ?? "");
    if (!questionId || !answerId) return;

    const screen = target.closest('[data-screen="question"]');
    if (!(screen instanceof HTMLElement)) return;

    const maxSelections = Number(screen.dataset.maxSelections ?? 1) || 1;
    const existing = Array.isArray(this.state.answers[questionId])
      ? this.state.answers[questionId]
      : [];
    const next = target.checked
      ? [...existing, answerId].slice(-maxSelections)
      : existing.filter((item) => item !== answerId);

    this.state.answers = {
      ...this.state.answers,
      [questionId]: uniqueValues(next),
    };

    this.#syncQuestionSelection(screen, questionId);
    this.#syncSubmitButtons();
    this.#persistQuizState("answer-change");
  };

  #handleClick = (event) => {
    const target = event.target;
    if (!(target instanceof HTMLElement)) return;

    // Intercept clicks on links starting with # inside the quiz to switch tabs
    const anchor = target.closest("a");
    if (anchor instanceof HTMLAnchorElement) {
      const href = anchor.getAttribute("href") ?? "";
      if (href.startsWith("#")) {
        const tabId = toSafeId(href.substring(1));
        const tabButton = this.#tabButtons.find(
          (btn) => btn instanceof HTMLElement && btn.dataset.tab === tabId,
        );
        if (tabButton instanceof HTMLElement) {
          event.preventDefault();
          if (
            tabButton.hidden ||
            tabButton.hasAttribute("data-routine-empty")
          ) {
            return;
          }
          const tabIndex = Number(tabButton.dataset.tabIndex ?? 0);
          if (!Number.isNaN(tabIndex)) {
            this.state.activeResultTab = tabIndex;
            this.#syncResultTabs();
            this.#persistQuizState("tab-change");
          }
          return;
        }
      }
    }

    if (target === this) {
      if (this.dataset.embedded === "true") return;
      this.dataset.visible = "false";
      return;
    }

    const action = target.closest("[data-action]");
    if (!(action instanceof HTMLElement)) return;

    const actionType = action.dataset.action;

    if (actionType === "start") {
      event.preventDefault();
      if (this.state.stepIndex !== -1) {
        skinQuizDebug("start-ignored-not-on-intro", {
          stepIndex: this.state.stepIndex,
        });
        return;
      }
      if (!this.#validateEmailField("continue")) {
        skinQuizDebug("start-blocked-email", {});
        return;
      }
      this.#showStep(0);
      return;
    }

    if (actionType === "next") {
      event.preventDefault();
      this.#goNext();
      return;
    }

    if (actionType === "submit-results") {
      event.preventDefault();
      void this.#submitResults(action);
      return;
    }

    if (actionType === "back") {
      event.preventDefault();
      if (action instanceof HTMLButtonElement && action.disabled) return;
      this.#goBack();
      return;
    }

    if (actionType === "tab") {
      if (
        action instanceof HTMLElement &&
        (action.hidden || action.hasAttribute("data-routine-empty"))
      ) {
        return;
      }
      const tabIndex = Number(action.dataset.tabIndex ?? 0);
      if (Number.isNaN(tabIndex)) return;
      this.state.activeResultTab = tabIndex;
      this.#syncResultTabs();
      this.#persistQuizState("tab-change");
      return;
    }

    if (actionType === "quiz-bundle-add") {
      event.preventDefault();
      void this.#handleQuizBundleAdd(action);
      return;
    }

    if (actionType === "quiz-redirect-tab") {
      event.preventDefault();
      this.#redirectToAllProductsTab();
      return;
    }

    if (actionType === "backdrop") {
      event.preventDefault();
      skinQuizDebug("modal-hide", { via: "backdrop" });
      this.dataset.visible = "false";
      return;
    }

    if (actionType === "close") {
      event.preventDefault();
      skinQuizDebug("modal-hide", { via: "close-button" });
      this.dataset.visible = "false";
      return;
    }

    if (actionType === "retake") {
      event.preventDefault();
      this.#resetQuiz();
    }
  };

  #handleKeydown = (event) => {
    if (event.key !== "Escape") return;
    if (this.dataset.embedded === "true") return;
    if (this.dataset.visible === "false") return;
    skinQuizDebug("modal-hide", { via: "escape" });
    this.dataset.visible = "false";
  };

  #goNext() {
    if (!this.#advanceAllowedForCurrentStep()) {
      this.#announceToLiveRegion(
        "Please select at least one answer to continue.",
      );
      skinQuizDebug("next-blocked-validation", {
        step: this.state.stepIndex,
      });
      return;
    }
    const questionCount = this.#questionScreens.length;
    if (this.state.stepIndex < questionCount - 1) {
      this.#showStep(this.state.stepIndex + 1);
    }
  }

  #goBack() {
    if (this.state.stepIndex <= 0) {
      this.#showStep(-1);
      return;
    }

    this.#showStep(this.state.stepIndex - 1);
  }

  #showStep(stepIndex) {
    this.state.stepIndex = stepIndex;

    for (const screen of this.#screens) {
      const screenStep = Number(screen.dataset.step ?? -99);
      screen.hidden = screenStep !== stepIndex;
    }

    for (const screen of this.#questionScreens) {
      if (!(screen instanceof HTMLElement)) continue;
      const qid = toSafeId(screen.dataset.questionId ?? "");
      if (qid) this.#syncQuestionSelection(screen, qid);
    }

    this.#syncBackNav();
    this.#syncSubmitButtons();
    this.#persistQuizState("step-change");
    queueMicrotask(() => {
      this.#syncAdvanceButtons();
    });

    if (this.refs.panel instanceof HTMLElement) {
      this.refs.panel.scrollTop = 0;
    }
  }

  /**
   * Back is only available once the customer has passed the intro (stepIndex >= 0).
   * On the intro (stepIndex -1), no prior step — mirrors disabled styling when a Back control exists.
   */
  #syncBackNav() {
    const canGoBack = this.state.stepIndex > -1;
    for (const btn of this.#backButtons) {
      if (!(btn instanceof HTMLButtonElement)) continue;
      btn.disabled = !canGoBack;
    }
  }

  #syncQuestionSelection(screen, questionId) {
    const selected = this.state.answers[questionId] ?? [];
    const inputs = [...screen.querySelectorAll('input[name="quiz-option"]')];

    for (const input of inputs) {
      if (!(input instanceof HTMLInputElement)) continue;
      const answerId = toSafeId(input.dataset.answerId ?? "");
      input.checked = !!answerId && selected.includes(answerId);
    }
  }

  #syncResultTabs() {
    if (this.#tabButtons.length > 0) {
      if (
        this.state.activeResultTab < 0 ||
        this.state.activeResultTab >= this.#tabButtons.length
      ) {
        this.state.activeResultTab = 0;
      }

      const activeBtn = this.#tabButtons[this.state.activeResultTab];
      if (activeBtn instanceof HTMLElement && activeBtn.hidden) {
        const firstVisible = this.#tabButtons.findIndex(
          (b) => b instanceof HTMLElement && !b.hidden,
        );
        if (firstVisible >= 0) {
          this.state.activeResultTab = firstVisible;
        }
      }
    }

    for (const tabButton of this.#tabButtons) {
      const index = Number(tabButton.dataset.tabIndex ?? 0);
      const isActive = index === this.state.activeResultTab;
      tabButton.classList.toggle("is-active", isActive);
      tabButton.setAttribute("aria-selected", isActive ? "true" : "false");
    }

    for (const panel of this.#tabPanels) {
      const panelTab = panel.dataset.tabPanel ?? "";
      const activeButton = this.#tabButtons[this.state.activeResultTab];
      const activeTab = activeButton?.dataset.tab ?? "";
      const routineEmpty =
        panel instanceof HTMLElement &&
        panel.hasAttribute("data-routine-empty");
      panel.hidden = routineEmpty || panelTab !== activeTab;
    }

    this.#syncResultTabDescription();

    if (this.refs.panel instanceof HTMLElement) {
      this.refs.panel.scrollTop = 0;
    }
  }

  /** Optional per-tab ritual copy from `tab.description` in metafield JSON; falls back to default result description. */
  #syncResultTabDescription() {
    const descEl = this.querySelector("[data-skin-quiz-result-description]");
    if (!(descEl instanceof HTMLElement)) return;

    const activeButton = this.#tabButtons[this.state.activeResultTab];
    const activeTabId = activeButton?.dataset.tab ?? "";
    const quiz = this.#quizzes[0];
    const tabs = Array.isArray(quiz?.result?.tabs) ? quiz.result.tabs : [];
    const activeTab = tabs.find((tab) => tab.id === activeTabId);
    const panel = this.#tabPanels.find(
      (p) => (p.dataset.tabPanel ?? "") === activeTabId,
    );

    // Prefer parsed quiz JSON — HTML links in `data-tab-description` attributes are fragile.
    const tabSpecific = toText(activeTab?.description ?? "");
    const tabFromAttr = (panel?.dataset.tabDescription ?? "").trim();
    const fallback =
      descEl.dataset.defaultDescription?.trim() ||
      toText(quiz?.result?.description ?? "") ||
      "";

    const text = tabSpecific || tabFromAttr || fallback;
    descEl.innerHTML = text;

    for (const a of descEl.querySelectorAll("a")) {
      const href = a.getAttribute("href") ?? "";
      if (href.startsWith("#")) {
        a.removeAttribute("target");
        a.removeAttribute("rel");
      } else {
        a.setAttribute("target", "_blank");
        a.setAttribute("rel", "noopener noreferrer");
      }
    }

    descEl.hidden = !text;
  }

  #populateComputedResults() {
    const quiz = this.#quizzes[0];
    if (!quiz) return null;

    const outcome = computeQuizResultState(quiz, this.state.answers);
    this.#applyResultOutcome(outcome, quiz);
    this.#syncRoutineEmptyAttributes(quiz, outcome.routineItemsByTab);
    this.#syncResultTabs();
    return { quiz, outcome };
  }

  async #submitResults(trigger) {
    if (!(trigger instanceof HTMLButtonElement)) return;
    if (!this.#validateCurrentQuestion()) return;
    if (!this.#validateEmailField("submit")) return;
    if (this.state.isSubmitting) return;

    const computed = this.#populateComputedResults();
    if (!computed) return;

    const questionCount = this.#questionScreens.length;
    if (this.state.hasSubmitted) {
      this.#showStep(questionCount);
      return;
    }
this.#showStep(questionCount);
    this.state.isSubmitting = true;
    this.#syncSubmitButtons();

    try {
      const response = await this.#submitQuizResult(
        computed.quiz,
        computed.outcome,
      );
      const submission =
        response && typeof response === "object" ? response.submission : null;
      const submissionId =
        submission && typeof submission === "object"
          ? submission.submissionId
          : response?.submissionId;
      const submittedAt =
        submission && typeof submission === "object"
          ? submission.submittedAt
          : response?.submittedAt;

      this.state.hasSubmitted = true;
      this.state.submissionId =
        submissionId === undefined || submissionId === null
          ? null
          : toText(String(submissionId));
      this.state.submittedAt =
        submittedAt === undefined || submittedAt === null
          ? null
          : toText(String(submittedAt));
      this.#clearSaveWarningUi();
      this.#syncQuizLineItemPropertiesInRoot(this);
      this.#showStep(questionCount);
      skinQuizDebug("submit-results-success", {
        submissionId: this.state.submissionId,
        stepIndex: questionCount,
      });
    } catch (error) {
      console.warn("Skin quiz submission failed", error);
      const raw =
        error instanceof Error && error.message.trim()
          ? error.message.trim()
          : "We couldn't save your quiz right now. Please try again.";
      const sessionHint =
        " Your quiz answers are still saved in this browser session until you close the tab.";
      const announced =
        raw.includes("email") ||
        raw.includes("Email") ||
        raw.includes("Invalid payload") ||
        raw.includes("quizId") ||
        raw.includes("required")
          ? raw
          : `${raw} ${sessionHint}`;
      const withResultsNote = `${announced} Your personalized results are shown below.`;
      this.#announceToLiveRegion(withResultsNote);
      const submitErr = this.querySelector("[data-skin-quiz-submit-error]");
      if (submitErr instanceof HTMLElement) {
        submitErr.textContent = announced;
        submitErr.hidden = false;
      }
      const saveWarn = this.querySelector("[data-skin-quiz-save-warning]");
      if (saveWarn instanceof HTMLElement) {
        saveWarn.textContent = announced;
        saveWarn.hidden = false;
      }

      this.#syncQuizLineItemPropertiesInRoot(this);
      this.#showStep(questionCount);

      const resultScreen = this.#resultScreen;
      if (resultScreen instanceof HTMLElement) {
        resultScreen.setAttribute("tabindex", "-1");
        requestAnimationFrame(() => {
          resultScreen.focus({ preventScroll: false });
        });
      }

      skinQuizDebug("submit-results-degraded", { message: raw });
    } finally {
      this.state.isSubmitting = false;
      this.#syncSubmitButtons();
    }
  }

  /**
   * @param {ReturnType<typeof normalizeQuiz>} quiz
   * @param {{ activeProfile: ResultProfile | null, routineItemsByTab: Map<string, RoutineDisplayItem[]> }} outcome
   */
  async #submitQuizResult(quiz, outcome) {
    const payload = this.#buildSubmissionPayload(quiz, outcome);
    const results = {
      payload,
      outcome: {
        activeProfile: outcome.activeProfile,
        routineItemsByTab: Object.fromEntries(outcome.routineItemsByTab),
        appliedRuleIds: outcome.appliedRuleIds ?? [],
        appliedRules: outcome.appliedRules ?? [],
      },
    };

    try {
      const response = await submitSkinQuizSubmission(payload, this);
      console.log("[skin-quiz] quiz submission", { ...results, response });
      return response;
    } catch (error) {
      console.log("[skin-quiz] quiz submission", {
        ...results,
        response: null,
        error,
      });
      throw error;
    }
  }

  #validateCurrentQuestion() {
    if (!this.#advanceAllowedForCurrentStep()) {
      this.#announceToLiveRegion(
        "Please select at least one answer to continue.",
      );
      skinQuizDebug("submit-results-blocked", {
        step: this.state.stepIndex,
      });
      return false;
    }
    return true;
  }

  #syncSubmitButtons() {
    const overlay = this.#submissionOverlay;
    if (overlay instanceof HTMLElement) {
      overlay.hidden = true;
    }

    const panel = this.refs.panel;
    if (panel instanceof HTMLElement) {
      panel.setAttribute("aria-busy", this.state.isSubmitting ? "true" : "false");
    }

    this.#syncAdvanceButtons();
  }

  #resetQuiz() {
    skinQuizDebug("quiz-reset", { via: "retake" });
    this.#clearPersistedQuizState();
    this.state.stepIndex = -1;
    this.state.answers = {};
    this.state.activeResultTab = 0;
    this.state.activeProfileId = "";
    this.state.email = "";
    this.state.hasSubmitted = false;
    this.state.isSubmitting = false;
    this.state.submissionId = null;
    this.state.submittedAt = null;
    this.#quizFormCloneSeq = 0;

    const emailInput = this.querySelector('input[name="quiz-email"]');
    if (emailInput instanceof HTMLInputElement) {
      emailInput.value = "";
    }

    this.#clearEmailErrorUi();
    this.#clearSaveWarningUi();

    const checkedInputs = this.querySelectorAll('input[name="quiz-option"]');
    for (const input of checkedInputs) {
      if (input instanceof HTMLInputElement) {
        input.checked = false;
      }
    }

    this.#resetResultContent();
    this.#syncQuizLineItemPropertiesInRoot(this);
    this.#showStep(-1);
    this.#syncResultTabs();
    this.#syncSubmitButtons();
  }

  #resetResultContent() {
    for (const btn of this.querySelectorAll('[data-action="tab"]')) {
      if (btn instanceof HTMLElement) {
        btn.removeAttribute("data-routine-empty");
        btn.hidden = false;
      }
    }
    for (const panel of this.querySelectorAll("[data-tab-panel]")) {
      if (panel instanceof HTMLElement) {
        panel.removeAttribute("data-routine-empty");
      }
    }

    const titleEl = this.querySelector("[data-skin-quiz-profile-title]");
    const descEl = this.querySelector("[data-skin-quiz-profile-description]");
    const imgEl = this.querySelector("[data-skin-quiz-profile-image]");
    const profileWrap = this.querySelector(
      "[data-skin-quiz-profile-image-wrap]",
    );
    const resultDesc = this.querySelector("[data-skin-quiz-result-description]");

    if (titleEl instanceof HTMLElement) {
      titleEl.textContent = this.#defaultProfileTitle;
    }

    if (descEl instanceof HTMLElement) {
      descEl.textContent = this.#defaultProfileDescription;
    }

    if (imgEl instanceof HTMLImageElement) {
      if (this.#defaultProfileImageSrc) {
        imgEl.src = this.#defaultProfileImageSrc;
      } else {
        imgEl.removeAttribute("src");
      }
      imgEl.alt = this.#defaultProfileImageAlt;
      imgEl.hidden = this.#defaultProfileImageHidden;

      if (profileWrap instanceof HTMLElement) {
        profileWrap.classList.toggle(
          "skin-quiz__image-wrap--placeholder",
          this.#defaultProfileImageHidden,
        );
      }
    }

    if (resultDesc instanceof HTMLElement) {
      const quiz = this.#quizzes[0];
      const fallback =
        resultDesc.dataset.defaultDescription?.trim() ||
        toText(quiz?.result?.description ?? "") ||
        "";
      resultDesc.innerHTML = fallback;

      for (const a of resultDesc.querySelectorAll("a")) {
        const href = a.getAttribute("href") ?? "";
        if (href.startsWith("#")) {
          a.removeAttribute("target");
          a.removeAttribute("rel");
        } else {
          a.setAttribute("target", "_blank");
          a.setAttribute("rel", "noopener noreferrer");
        }
      }

      resultDesc.hidden = !fallback;
    }

    for (const host of this.querySelectorAll("[data-result-visuals]")) {
      if (host instanceof HTMLElement) {
        host.innerHTML = "";
      }
    }

    for (const host of this.querySelectorAll("[data-result-details]")) {
      if (host instanceof HTMLElement) {
        host.innerHTML = "";
      }
    }

    for (const bundle of this.querySelectorAll("[data-skin-quiz-bundle]")) {
      if (!(bundle instanceof HTMLElement)) continue;
      bundle.hidden = true;
      const compare = bundle.querySelector("[data-bundle-compare]");
      const sale = bundle.querySelector("[data-bundle-sale]");
      if (compare instanceof HTMLElement) {
        compare.textContent = "";
        compare.hidden = true;
      }
      if (sale instanceof HTMLElement) {
        sale.textContent = "";
      }
    }
  }

  /**
   * @param {ReturnType<typeof normalizeQuiz>} quiz
   * @param {{ activeProfile: ResultProfile | null, routineItemsByTab: Map<string, RoutineDisplayItem[]> }} outcome
   */
  #buildSubmissionPayload(quiz, outcome) {
    const tabs = Array.isArray(quiz?.result?.tabs) ? quiz.result.tabs : [];
    const activeTab = tabs[this.state.activeResultTab] ?? tabs[0] ?? null;
    const activeTabId = activeTab?.id ?? "";
    const activeTabItems = outcome.routineItemsByTab.get(activeTabId) ?? [];

    return {
      email: toText(this.state.email),
      quizId: toSafeId(quiz.id || this.dataset.quizId || "skin-quiz"),
      answers: this.state.answers,
      result: {
        profileId: outcome.activeProfile?.id ?? "",
        profileLabel: outcome.activeProfile?.title ?? "",
        recommendedProducts: activeTabItems.map((item) => item.productKey),
        activeTabId,
        routines: [...outcome.routineItemsByTab.entries()].map(
          ([tabId, items]) => ({
            tabId,
            products: items.map((item) => ({
              productKey: item.productKey,
              label: item.label,
              stepType: item.stepType ?? "",
            })),
          }),
        ),
      },
    };
  }

  /**
   * Applies computed profile + routine lists to Liquid-rendered result containers.
   * @param {{ activeProfile: ResultProfile | null, routineItemsByTab: Map<string, RoutineDisplayItem[]> }} outcome
   * @param {ReturnType<typeof normalizeQuiz>} quiz
   */
  #applyResultOutcome(outcome, quiz) {
    const { activeProfile, routineItemsByTab } = outcome;
    this.state.activeProfileId = activeProfile?.id ?? "";

    const titleEl = this.querySelector("[data-skin-quiz-profile-title]");
    const descEl = this.querySelector("[data-skin-quiz-profile-description]");
    const imgEl = this.querySelector("[data-skin-quiz-profile-image]");
    const profileWrap = this.querySelector(
      "[data-skin-quiz-profile-image-wrap]",
    );

    if (activeProfile) {
      if (titleEl) {
        titleEl.textContent = activeProfile.title ?? "";
      }

      if (descEl) {
        descEl.textContent = activeProfile.description ?? "";
      }

      if (imgEl instanceof HTMLImageElement) {
        const src = activeProfile.summaryImage?.src ?? "";
        const alt =
          activeProfile.summaryImage?.alt ?? activeProfile.title ?? "";

        if (src) {
          imgEl.src = src;
          imgEl.alt = alt;
          imgEl.hidden = false;
          profileWrap?.classList.remove("skin-quiz__image-wrap--placeholder");
        } else {
          imgEl.removeAttribute("src");
          imgEl.alt = "";
          imgEl.hidden = true;
          profileWrap?.classList.add("skin-quiz__image-wrap--placeholder");
        }
      }
    }

    const tabs = Array.isArray(quiz?.result?.tabs) ? quiz.result.tabs : [];
    for (const tab of tabs) {
      const tabId = tab?.id;
      if (!tabId) continue;
      const isShowAll = tab?.showAllProducts === true;

      const visualsHost = this.querySelector(
        `[data-result-visuals="${cssEscape(tabId)}"]`,
      );
      const detailsHost = this.querySelector(
        `[data-result-details="${cssEscape(tabId)}"]`,
      );
      if (!(detailsHost instanceof HTMLElement)) continue;

      const items = routineItemsByTab.get(tabId) ?? [];
      if (visualsHost instanceof HTMLElement) visualsHost.innerHTML = "";
      detailsHost.innerHTML = "";

      for (let i = 0; i < items.length; i++) {
        const item = items[i];
        if (isShowAll) {
          const card = this.#cloneQuizProductCard(item.productKey);
          if (card) {
            card.classList.remove("skin-quiz__product-card--registry");
            card.removeAttribute("hidden");
            const visual = card.querySelector("[data-quiz-product-visual]");
            const detail = card.querySelector("[data-quiz-product-detail]");
            if (visual instanceof HTMLElement) {
              this.#decorateQuizProductVisual(visual, i);
            }
            if (detail instanceof HTMLElement) {
              this.#decorateQuizProductDetail(detail, item, i);
            }
            const li = document.createElement("li");
            li.className = "skin-quiz__routine__detail-item";
            li.appendChild(card);
            detailsHost.appendChild(li);
          } else {
            const li = document.createElement("li");
            li.className = "skin-quiz__routine__detail-item skin-quiz__result-item--fallback";
            li.textContent = item.label;
            detailsHost.appendChild(li);
          }
        } else {
          const visual = this.#cloneQuizProductVisual(item.productKey);
          const detail = this.#cloneQuizProductDetail(item.productKey);

          if (
            visual instanceof HTMLElement &&
            visualsHost instanceof HTMLElement
          ) {
            this.#decorateQuizProductVisual(visual, i);
            visual.classList.remove("skin-quiz__product-card--registry");
            visualsHost.appendChild(visual);
          } else if (item.productKey) {
            skinQuizDevWarn(
              "registry-visual",
              "Could not clone product visual from registry (missing card or visual slot).",
              item.productKey,
            );
          }

          if (detail) {
            this.#decorateQuizProductDetail(detail, item, i);
            detail.classList.remove("skin-quiz__product-card--registry");
            const li = document.createElement("li");
            li.className = "skin-quiz__routine__detail-item";
            li.appendChild(detail);
            detailsHost.appendChild(li);
          } else {
            const li = document.createElement("li");
            li.className =
              "skin-quiz__routine__detail-item skin-quiz__result-item--fallback";
            li.textContent = item.label;
            detailsHost.appendChild(li);
          }
        }
      }

      this.#updateBundleStrip(tabId, items);
    }

    this.#syncQuizLineItemPropertiesInRoot(this);
  }

  /**
   * Hide tabs/panels whose evaluated routine has no products (see `[data-routine-empty]` CSS).
   * If every tab is empty, clears markers so the shopper still sees a predictable shell.
   * @param {ReturnType<typeof normalizeQuiz>} quiz
   * @param {Map<string, RoutineDisplayItem[]>} routineItemsByTab
   */
  #syncRoutineEmptyAttributes(quiz, routineItemsByTab) {
    for (const btn of this.#tabButtons) {
      if (btn instanceof HTMLElement) {
        btn.removeAttribute("data-routine-empty");
        btn.hidden = false;
      }
    }
    for (const panel of this.#tabPanels) {
      if (panel instanceof HTMLElement) {
        panel.removeAttribute("data-routine-empty");
      }
    }

    const tabs = Array.isArray(quiz?.result?.tabs) ? quiz.result.tabs : [];
    /** @type {string[]} */
    const markedEmpty = [];

    for (const tab of tabs) {
      const tabId = tab?.id;
      if (!tabId) continue;

      const items = routineItemsByTab.get(tabId) ?? [];
      if (items.length > 0) continue;

      markedEmpty.push(tabId);

      const panel = this.querySelector(
        `[data-tab-panel="${cssEscape(tabId)}"]`,
      );
      const tabBtn = this.querySelector(
        `[data-action="tab"][data-tab="${cssEscape(tabId)}"]`,
      );

      if (panel instanceof HTMLElement) {
        panel.setAttribute("data-routine-empty", "");
      }
      if (tabBtn instanceof HTMLElement) {
        tabBtn.setAttribute("data-routine-empty", "");
        tabBtn.hidden = true;
      }
    }

    if (tabs.length > 0 && markedEmpty.length === tabs.length) {
      for (const btn of this.#tabButtons) {
        if (btn instanceof HTMLElement) {
          btn.removeAttribute("data-routine-empty");
          btn.hidden = false;
        }
      }
      for (const panel of this.#tabPanels) {
        if (panel instanceof HTMLElement) {
          panel.removeAttribute("data-routine-empty");
        }
      }
    }
  }

  /**
   * Fills hidden line item property inputs inside Horizon product forms (quiz cards).
   * @param {ParentNode} root
   */
  #syncQuizLineItemPropertiesInRoot(root) {
    for (const detail of root.querySelectorAll("[data-quiz-product-detail]")) {
      if (!(detail instanceof HTMLElement)) continue;
      const productKey = detail.dataset.productKey?.trim() ?? "";
      const routineHost = detail.closest("[data-result-details]");
      const routineTabId =
        routineHost instanceof Element
          ? routineHost.getAttribute("data-result-details")?.trim() ?? ""
          : "";
      const props = this.#buildQuizLineItemProperties({
        routineTabId,
        productKey,
      });
      for (const input of detail.querySelectorAll("[data-quiz-line-property]")) {
        if (!(input instanceof HTMLInputElement)) continue;
        const key = input.getAttribute("data-quiz-line-property");
        if (!key) continue;
        input.value = props[key] ?? "";
      }
    }
  }

  /**
   * @param {string} tabId
   * @param {RoutineDisplayItem[]} routineItems
   */
  #updateBundleStrip(tabId, routineItems) {
    const bundle = this.querySelector(
      `[data-skin-quiz-bundle="${cssEscape(tabId)}"]`,
    );
    if (!(bundle instanceof HTMLElement)) return;

    const btn = bundle.querySelector('[data-action="quiz-bundle-add"]');
    const redirectBtn = bundle.querySelector('[data-action="quiz-redirect-tab"]');
    const ctaLabel = btn?.dataset.bundleCtaLabel?.trim() || "";

    if (routineItems.length === 0 || (!ctaLabel && !redirectBtn)) {
      bundle.hidden = true;
      return;
    }

    bundle.hidden = false;

    const eyebrowEl = bundle.querySelector("[data-bundle-eyebrow]");
    const compareEl = bundle.querySelector("[data-bundle-compare]");
    const saleEl = bundle.querySelector("[data-bundle-sale]");

    let sumPrice = 0;
    for (const item of routineItems) {
      const card = this.#getRegistryCard(item.productKey);
      const detail = card?.querySelector("[data-quiz-product-detail]");
      if (!(detail instanceof HTMLElement)) continue;
      const price = Number(detail.dataset.linePriceCents ?? 0) || 0;
      const compare = Number(detail.dataset.lineCompareCents ?? 0) || 0;
      const retail = compare > price ? compare : price;
      sumPrice += retail;
    }

    const pctRaw = Number(bundle.dataset.bundleDiscountPercent ?? this.dataset.bundleDiscountPercent ?? NaN);
    const pct = Number.isFinite(pctRaw) && pctRaw > 0 ? pctRaw : 0;
    const youPay = pct > 0 ? Math.round(sumPrice * (1 - pct / 100)) : sumPrice;

    const promoE = bundle.dataset.bundlePromoEyebrow?.trim();
    if (eyebrowEl) {
      eyebrowEl.textContent = promoE ?? "";
    }

    const moneyFormat = this.dataset.moneyFormat ?? "{{amount}}";
    const currency = this.dataset.currency ?? "USD";

    if (compareEl instanceof HTMLElement) {
      if (pct > 0 && sumPrice > youPay) {
        compareEl.textContent = formatMoney(sumPrice, moneyFormat, currency);
        compareEl.hidden = false;
      } else {
        compareEl.textContent = "";
        compareEl.hidden = true;
      }
    }

    if (saleEl) {
      saleEl.textContent = formatMoney(youPay, moneyFormat, currency);
    }

    if (btn instanceof HTMLButtonElement) {
      if (ctaLabel) {
        const quiz = this.#quizzes[0];
        const tabs = Array.isArray(quiz?.result?.tabs) ? quiz.result.tabs : [];
        const currentTab = tabs.find((t) => t?.id === tabId);
        const isShowAll = currentTab?.showAllProducts === true;

        if (isShowAll) {
          const compareFormatted = formatMoney(sumPrice, moneyFormat, currency);
          const saleFormatted = formatMoney(youPay, moneyFormat, currency);

          if (pct > 0 && sumPrice > youPay) {
            btn.innerHTML = `<span class="skin-quiz__button-label">${ctaLabel}</span><span class="skin-quiz__button-price-wrap"><span class="skin-quiz__button-dash">&mdash;</span><span class="skin-quiz__button-compare">${compareFormatted}</span><span class="skin-quiz__button-sale">${saleFormatted}</span></span>`;
          } else {
            btn.innerHTML = `<span class="skin-quiz__button-label">${ctaLabel}</span><span class="skin-quiz__button-price-wrap"><span class="skin-quiz__button-dash">&mdash;</span><span class="skin-quiz__button-sale">${saleFormatted}</span></span>`;
          }
        } else {
          btn.textContent = ctaLabel;
        }
      }

      /** @type {{ addable: number, unavailable: number, invalid_variant: number, no_registry: number }} */
      const counts = {
        addable: 0,
        unavailable: 0,
        invalid_variant: 0,
        no_registry: 0,
      };
      /** @type {{ productKey: string, reason: string }[]} */
      const excluded = [];

      for (const item of routineItems) {
        const card = this.#getRegistryCard(item.productKey);
        const d = card?.querySelector("[data-quiz-product-detail]");
        const { addable, reason } = classifyQuizDetailForBundle(
          d instanceof HTMLElement ? d : null,
        );
        if (addable) {
          counts.addable += 1;
        } else {
          counts[reason] += 1;
          excluded.push({ productKey: toText(item.productKey ?? ""), reason });
        }
      }

      const bundleable = counts.addable > 0;
      btn.disabled = !bundleable;

      const helper = bundle.querySelector("[data-bundle-helper]");
      if (helper instanceof HTMLElement) {
        btn.removeAttribute("aria-describedby");
        if (bundleable) {
          helper.hidden = true;
          helper.textContent = "";
        } else if (routineItems.length > 0) {
          const onlyUnavailable =
            counts.addable === 0 &&
            counts.unavailable === routineItems.length &&
            counts.invalid_variant === 0 &&
            counts.no_registry === 0;
          if (onlyUnavailable) {
            helper.textContent =
              "Nothing in this routine is in stock for a quick add. Try again later or add items individually when available.";
          } else {
            helper.textContent =
              "Add routine needs at least one in-stock variant. Check that products are linked in the quiz registry.";
          }
          helper.hidden = false;
          if (helper.id) btn.setAttribute("aria-describedby", helper.id);
        } else {
          helper.hidden = true;
          helper.textContent = "";
        }
      }

      skinQuizDebug("bundle-eligibility-tab", {
        tabId,
        bundleable,
        counts,
        excluded: excluded.slice(0, 24),
      });
    }

    if (redirectBtn instanceof HTMLButtonElement) {
      redirectBtn.disabled = false;
      const helper = bundle.querySelector("[data-bundle-helper]");
      if (helper instanceof HTMLElement) {
        helper.hidden = true;
        helper.textContent = "";
      }
      redirectBtn.removeAttribute("aria-describedby");
    }
  }

  /**
   * @param {HTMLElement} trigger
   */
  async #handleQuizBundleAdd(trigger) {
    if (!(trigger instanceof HTMLButtonElement)) return;
    if (this.#bundleCartAddLocked) return;
    const tabId = trigger.dataset.tabId ?? "";
    if (!tabId) return;

    const list = this.querySelector(
      `[data-result-details="${cssEscape(tabId)}"]`,
    );
    if (!list) return;

    this.#bundleCartAddLocked = true;
    try {
      const details = [...list.querySelectorAll("[data-quiz-product-detail]")];
      /** @type {{ variantId: string, quantity: number, properties?: Record<string, string>, productKey: string, productHandle: string }[]} */
      const lineItems = [];
      const seenVariantIds = new Set();

      for (const d of details) {
        if (!(d instanceof HTMLElement)) continue;
        const classified = classifyQuizDetailForBundle(d);
        if (!classified.addable) {
          skinQuizDebug("bundle-add-excluded", {
            why: classified.reason,
            productKey: d.dataset.productKey,
            handle: d.dataset.productHandle,
            variantId: d.dataset.variantId,
          });
          if (classified.reason === "invalid_variant") {
            skinQuizDevWarn(
              "bundle-skip-variant",
              "Routine row has missing or invalid variant resolution (bundle add skipped for this row).",
              {
                productKey: d.dataset.productKey,
                handle: d.dataset.productHandle,
              },
            );
          }
          continue;
        }

        const variantKey = classified.resolvedVariantId ?? "";
        if (!variantKey) continue;

        skinQuizDebug("bundle-add-line-resolved", {
          productKey: d.dataset.productKey,
          variantId: variantKey,
          pick: classified.pick,
        });

        if (seenVariantIds.has(variantKey)) {
          skinQuizDebug("bundle-add-dedupe-variant", {
            variantId: variantKey,
            productKey: d.dataset.productKey,
          });
          continue;
        }
        seenVariantIds.add(variantKey);

        const productKey = d.dataset.productKey?.trim() ?? "";
        const productHandle = d.dataset.productHandle?.trim() ?? "";
        const props = compactLineProperties(
          this.#buildQuizLineItemProperties({
            routineTabId: tabId,
            productKey,
          }),
        );

        lineItems.push({
          variantId: variantKey,
          quantity: 1,
          properties: props,
          productKey,
          productHandle,
        });
      }

      skinQuizDebug("bundle-add-payload-built", {
        tabId,
        lineCount: lineItems.length,
        variantIds: lineItems.map((row) => row.variantId),
        keys: lineItems.map((row) => row.productKey),
      });

      if (lineItems.length === 0) {
        this.#announceToLiveRegion(
          "No bundle items are available to add right now.",
        );
        return;
      }

      await this.#addVariantsToCart(
        lineItems,
        trigger,
        "skin-quiz-bundle-add",
      );
    } finally {
      this.#bundleCartAddLocked = false;
    }
  }

  #redirectToAllProductsTab() {
    const quiz = this.#quizzes[0];
    if (!quiz) return;
    const tabs = Array.isArray(quiz?.result?.tabs) ? quiz.result.tabs : [];

    let targetIndex = tabs.findIndex((t) => t?.showAllProducts === true);
    if (targetIndex === -1) {
      targetIndex = 0;
    }

    if (targetIndex < 0 || targetIndex >= this.#tabButtons.length) return;

    this.state.activeResultTab = targetIndex;
    this.#syncResultTabs();
    this.#persistQuizState("tab-change");
  }

  /**
   * @param {{ variantId: string, quantity: number, properties?: Record<string, string>, productKey?: string, productHandle?: string }[]} items
   * @param {HTMLButtonElement} trigger
   * @param {string} source
   */
  async #addVariantsToCart(items, trigger, source) {
    const isBundleBtn = trigger.matches('[data-action="quiz-bundle-add"]');
    const originalLabel =
      trigger.dataset.originalLabel ?? (isBundleBtn ? trigger.innerHTML : trigger.textContent)?.trim() ?? "";
    trigger.dataset.originalLabel = originalLabel;
    trigger.disabled = true;
    trigger.dataset.loading = "true";
    trigger.setAttribute("aria-busy", "true");
    if (isBundleBtn) {
      trigger.textContent = "Adding...";
    }
    skinQuizDebug("bundle-cart-add-state-before", {
      stepIndex: this.state.stepIndex,
      hasSubmitted: this.state.hasSubmitted,
    });
    try {
      const payloadRows = items.map((row) => ({
        variantId: row.variantId,
        quantity: row.quantity,
        properties: row.properties,
      }));
      const result = await postCartLineItemsBulk(payloadRows, items);

      skinQuizDebug("bundle-add-cart-result", {
        source,
        addedCount: result.addedCount,
        failureCount: result.failures.length,
        failures: result.failures.map((f) => ({
          productKey: f.item?.productKey,
          handle: f.item?.productHandle,
          variantId: f.item?.variantId,
          message: f.data?.message,
        })),
      });

      if (result.addedCount === 0) {
        trigger.dataset.loading = "false";
        trigger.dataset.state = "error";
        if (isBundleBtn) {
          trigger.textContent = "Try again";
        }
        const first = result.failures[0]?.data ?? {
          message: "Could not add to cart",
          description: "",
          errors: {},
        };
        document.dispatchEvent(
          new CartErrorEvent(
            "skin-quiz",
            first.message ?? "Could not add to cart",
            first.description ?? "",
            first.errors ?? {},
          ),
        );
        this.#announceToLiveRegion(first.message || "");
        return;
      }

      if (result.failures.length > 0) {
        const hint = result.failures
          .map(
            (f) =>
              f.item?.productKey ||
              f.item?.productHandle ||
              f.item?.variantId ||
              "item",
          )
          .join(", ");
        skinQuizDevWarn(
          "bundle-add-partial",
          "Some routine products were not added (see debug mode for details).",
          result.failures,
        );
        this.#announceToLiveRegion(
          `Added ${result.addedCount} item(s). Some products could not be added: ${hint}.`,
        );
      } else {
        const addedText =
          typeof Theme !== "undefined" && Theme.translations?.added
            ? Theme.translations.added
            : "Added to cart";
        this.#announceToLiveRegion(addedText);
      }

      trigger.dataset.loading = "false";
      trigger.dataset.state = "success";
      if (isBundleBtn) {
        trigger.textContent =
          result.failures.length > 0 ? "Partially added" : "Added";
      }

      document.dispatchEvent(
        new CartAddEvent({}, "skin-quiz", {
          source: "product-form-component",
          itemCount: result.addedCount,
          sections: result.lastSuccessData?.sections,
        }),
      );
      skinQuizDebug("bundle-add-cart-success", {
        source,
        itemCount: result.addedCount,
        partial: result.failures.length > 0,
      });
    } catch (err) {
      console.error(err);
      trigger.dataset.loading = "false";
      trigger.dataset.state = "error";
      if (isBundleBtn) {
        trigger.textContent = "Try again";
      }
      document.dispatchEvent(
        new CartErrorEvent(
          "skin-quiz",
          "Could not add to cart",
          err instanceof Error ? err.message : String(err),
          {},
        ),
      );
    } finally {
      skinQuizDebug("bundle-cart-add-finally-quiz-state", {
        stepIndex: this.state.stepIndex,
        hasSubmitted: this.state.hasSubmitted,
      });
      this.#scheduleQuizDomResync("skin-quiz-bundle-add");
      window.setTimeout(() => {
        trigger.disabled = false;
        trigger.removeAttribute("aria-busy");
        trigger.dataset.loading = "false";
        delete trigger.dataset.state;
        if (isBundleBtn) {
          trigger.innerHTML = trigger.dataset.originalLabel ?? originalLabel;
        }
      }, 700);
    }
  }

  /**
   * Line item properties for quiz attribution (single-card forms + bundle JSON).
   * @param {{ routineTabId?: string, productKey?: string }} [opts]
   * @returns {Record<string, string>}
   */
  #buildQuizLineItemProperties(opts = {}) {
    const routineTabId = toText(opts.routineTabId ?? "");
    const productKey = toText(opts.productKey ?? "");
    const quizId = toSafeId(this.dataset.quizId || "skin-quiz");
    const profileId = toSafeId(this.state.activeProfileId || "");
    const submissionId =
      this.state.submissionId === undefined || this.state.submissionId === null
        ? ""
        : String(this.state.submissionId);

    /** @type {Record<string, string>} */
    const out = {
      _skin_quiz: quizId,
      _skin_quiz_profile: profileId,
      _skin_quiz_routine: routineTabId,
      _skin_quiz_product_key: productKey,
    };
    if (submissionId) {
      out._skin_quiz_submission_id = submissionId;
    }
    return out;
  }

  /**
   * @param {string} message
   */
  #announceToLiveRegion(message) {
    const region = this.querySelector("[data-skin-quiz-live-region]");
    if (!(region instanceof HTMLElement) || !message) return;
    region.textContent = "";
    window.requestAnimationFrame(() => {
      region.textContent = message;
    });
  }

  /**
   * @param {string} productKey
   * @returns {HTMLElement | null}
   */
  #getRegistryCard(productKey) {
    const registry = this.#productRegistry;
    if (!registry || !productKey) return null;
    const key = cssEscape(toSafeId(productKey));
    const card = registry.querySelector(
      `[data-quiz-product-card][data-product-key="${key}"]`,
    );
    return card instanceof HTMLElement ? card : null;
  }

  /**
   * @param {string} productKey
   * @returns {HTMLElement | null}
   */
  #cloneQuizProductCard(productKey) {
    const card = this.#getRegistryCard(productKey);
    if (!card) return null;
    const clone = /** @type {HTMLElement} */ (card.cloneNode(true));
    return clone;
  }

  /**
   * @param {string} productKey
   * @returns {HTMLElement | null}
   */
  #cloneQuizProductVisual(productKey) {
    const card = this.#getRegistryCard(productKey);
    const source = card?.querySelector("[data-quiz-product-visual]");
    if (!(source instanceof HTMLElement)) return null;
    const clone = /** @type {HTMLElement} */ (source.cloneNode(true));
    return clone;
  }

  /**
   * @param {string} productKey
   * @returns {HTMLElement | null}
   */
  #cloneQuizProductDetail(productKey) {
    const card = this.#getRegistryCard(productKey);
    const source = card?.querySelector("[data-quiz-product-detail]");
    if (!(source instanceof HTMLElement)) return null;
    const clone = /** @type {HTMLElement} */ (source.cloneNode(true));
    return clone;
  }

  /**
   * @param {HTMLElement} visual
   * @param {number} index
   */
  #decorateQuizProductVisual(visual, index) {
    const idxEl = visual.querySelector("[data-quiz-visual-index]");
    if (!(idxEl instanceof HTMLElement)) return;
    const n = String(index + 1).padStart(2, "0");
    idxEl.textContent = `[${n}]`;
  }

  /**
   * @param {HTMLElement} detail
   * @param {RoutineDisplayItem} item
   * @param {number} index
   */
  #decorateQuizProductDetail(detail, item, index) {
    const stepEl = detail.querySelector("[data-quiz-product-step]");
    if (!(stepEl instanceof HTMLElement)) return;

    const form = detail.querySelector('form[data-type="add-to-cart-form"]');
    if (form instanceof HTMLFormElement && form.id) {
      this.#quizFormCloneSeq += 1;
      form.id = `${form.id}-result-${this.#quizFormCloneSeq}`;
    }

    const n = String(index + 1).padStart(2, "0");
    const st = toText(item.stepType ?? "");

    if (st) {
      stepEl.textContent = `[${n}] ${st.toUpperCase()}`;
    } else {
      stepEl.textContent = `[${n}]`;
    }
    stepEl.hidden = false;
  }

  #getRawQuizPayload() {
    const script = this.querySelector("[data-skin-quiz-data]");
    if (!(script instanceof HTMLScriptElement)) return null;
    const json = script.textContent?.trim();
    if (!json) return null;

    try {
      return JSON.parse(json);
    } catch {
      return null;
    }
  }
}

customElements.define("skin-quiz-component", SkinQuizComponent);

function normalizeQuizCollection(payload) {
  const candidates = Array.isArray(payload)
    ? payload
    : Array.isArray(payload?.quizzes)
      ? payload.quizzes
      : payload && typeof payload === "object"
        ? Object.values(payload)
        : [];

  return candidates
    .map((quiz, index) => normalizeQuiz(quiz, index))
    .filter((quiz) => quiz.questions.length > 0);
}

function normalizeQuiz(quiz, index) {
  const intro = quiz?.intro ?? quiz?.start ?? {};
  const questionsSource = Array.isArray(quiz?.questions)
    ? quiz.questions
    : Array.isArray(quiz?.steps)
      ? quiz.steps.filter((step) => step?.type !== "result")
      : [];
  const result = quiz?.result ?? quiz?.results ?? {};
  const resultLayout = quiz?.resultLayout ?? {};

  const products = normalizeProductsFromQuiz(quiz);

  return {
    id: toSafeId(quiz?.id ?? quiz?.handle ?? `quiz-${index + 1}`),
    intro: {
      eyebrow: toText(intro.eyebrow),
      quizLabel: toText(intro.quizLabel ?? quiz?.label),
      title: toText(intro.title ?? intro.headline ?? intro.heading),
      description: toText(
        intro.description ?? intro.subheading ?? intro.body ?? "",
      ),
      emailLabel: toText(intro.emailLabel),
      emailPlaceholder: toText(intro.emailPlaceholder),
      ctaLabel: toText(
        intro.buttonLabel ??
          intro.startButtonLabel ??
          intro.ctaLabel ??
          intro.cta?.label,
      ),
      termsText: toText(intro.termsText ?? intro.disclaimer ?? ""),
      image: resolveQuizImageSrc(
        intro && typeof intro === "object" ? intro : {},
      ),
    },
    questions: questionsSource.map((question, questionIndex) =>
      normalizeQuestion(question, questionIndex),
    ),
    resultProfiles: normalizeResultProfiles(quiz?.resultProfiles),
    routineTemplates: normalizeRoutineTemplates(quiz?.routineTemplates),
    products,
    result: {
      title: toText(resultLayout.title || result.title),
      description: toText(
        resultLayout.description ?? result.description ?? "",
      ),
      tabs: normalizeTabs(result, resultLayout),
      rules: normalizeRules(quiz?.rules),
      /** @deprecated use `products` on normalized quiz — kept for older call sites */
      productMappings: products,
    },
  };
}

function normalizeQuestion(question, index) {
  const optionsSource = Array.isArray(question?.answers)
    ? question.answers
    : Array.isArray(question?.options)
      ? question.options
      : [];

  const selectionObj =
    question?.selection && typeof question.selection === "object"
      ? question.selection
      : null;

  const selectionTypeRaw = toText(
    question?.selectionType ?? selectionObj?.type ?? question?.type ?? "single",
  );

  const multiselect = isMultiselectQuestionType(selectionTypeRaw);

  const parsedMax = Number(
    question?.maxSelections ?? question?.max_selection ?? selectionObj?.max,
  );
  let maxSelections =
    Number.isFinite(parsedMax) && parsedMax > 0 ? Math.floor(parsedMax) : NaN;
  if (!Number.isFinite(maxSelections)) {
    maxSelections = multiselect ? 2 : 1;
  }
  if (!multiselect) {
    maxSelections = 1;
  }

  const imageSrc = resolveQuizImageSrc(
    question && typeof question === "object" ? question : {},
  );

  const optional =
    question?.optional === true || question?.required === false;

  return {
    id: toSafeId(question?.id ?? question?.key ?? `question-${index + 1}`),
    title: toText(
      question?.title ??
        question?.question ??
        question?.headline ??
        question?.label ??
        "",
    ),
    optional,
    image: imageSrc,
    maxSelections,
    selectionTypeNormalized: multiselect ? "multiple" : "single",
    selectionTypeRaw: selectionTypeRaw || "single",
    options: optionsSource.map((option, optionIndex) => ({
      id: toSafeId(option?.id ?? option?.key ?? `option-${optionIndex + 1}`),
      label: toText(option?.label ?? option?.title ?? option?.value ?? ""),
    })),
  };
}

function normalizeTabs(result, resultLayout) {
  const tabsSource = Array.isArray(resultLayout?.tabs)
    ? resultLayout.tabs
    : Array.isArray(result?.tabs)
      ? result.tabs
      : Array.isArray(result?.routines)
        ? result.routines
        : [];

  if (tabsSource.length > 0) {
    return tabsSource.map((tab, index) => ({
      id: toSafeId(tab?.id ?? tab?.key ?? `tab-${index + 1}`),
      label: toText(tab?.label ?? tab?.title ?? `Tab ${index + 1}`),
      image: resolveQuizImageSrc(
        tab && typeof tab === "object" ? tab : {},
      ),
      description: toText(tab?.description ?? ""),
      items: normalizeTabItems(tab),
      showAllProducts: tab?.showAllProducts === true,
    }));
  }

  return [];
}

function normalizeRules(rules) {
  if (!Array.isArray(rules)) return [];

  return rules.map((rule) => {
    const conditions = Array.isArray(rule?.conditions) ? rule.conditions : [];
    const actions = Array.isArray(rule?.actions) ? rule.actions : [];
    const priorityRaw = Number(rule?.priority);
    const priority = Number.isFinite(priorityRaw) ? priorityRaw : 999;

    return {
      id: toSafeId(rule?.id ?? rule?.key ?? ""),
      priority,
      isFallback:
        rule?.isFallback === true ||
        rule?.fallback === true ||
        rule?.is_fallback === true,
      conditions: conditions.map((condition) => ({
        questionId: toSafeId(condition?.questionId ?? ""),
        answerId: toSafeId(condition?.answerId ?? ""),
      })),
      actions: normalizeRuleActions(actions),
    };
  });
}

/**
 * @param {unknown[]} actions
 * @returns {NormalizedRuleAction[]}
 */
function normalizeRuleActions(actions) {
  const out = [];

  for (const action of actions) {
    const type = toText(action?.type);

    if (type === "ADD_ROUTINE_STEP") {
      out.push({
        type: "ADD_ROUTINE_STEP",
        routine: toSafeId(action?.routine ?? ""),
        productKey: toSafeId(action?.step?.productKey ?? ""),
        stepType: toText(action?.step?.type ?? ""),
      });
      continue;
    }

    if (type === "SET_PROFILE") {
      out.push({
        type: "SET_PROFILE",
        profileId: toSafeId(action?.profileId ?? ""),
      });
      continue;
    }

    if (type === "ADD_ROUTINE_TEMPLATE") {
      out.push({
        type: "ADD_ROUTINE_TEMPLATE",
        templateId: toSafeId(action?.templateId ?? ""),
      });
    }
  }

  return out;
}

function normalizeResultProfiles(profiles) {
  if (!Array.isArray(profiles)) return [];

  return profiles.map((profile) => ({
    id: toSafeId(profile?.id ?? ""),
    title: toText(profile?.title ?? ""),
    description: toText(profile?.description ?? ""),
    summaryImage: {
      src: toText(profile?.summaryImage?.src ?? profile?.summaryImage ?? ""),
      alt: toText(profile?.summaryImage?.alt ?? ""),
    },
  }));
}

function normalizeRoutineTemplates(templates) {
  if (!Array.isArray(templates)) return [];

  return templates.map((template) => ({
    id: toSafeId(template?.id ?? ""),
    routine: toSafeId(template?.routine ?? ""),
    steps: Array.isArray(template?.steps)
      ? template.steps.map((step) => ({
          type: toText(step?.type ?? ""),
          productKey: toSafeId(step?.productKey ?? ""),
        }))
      : [],
  }));
}

/**
 * Admin catalog order (#1, #2, …) from published quiz JSON.
 * @param {object} item
 * @param {number} [listIndex] 0-based index in products[] when priority is omitted
 * @returns {number}
 */
function resolveProductCatalogPriority(item, listIndex = 0) {
  const priorityRaw = Number(item?.priority ?? item?.product?.priority);
  if (Number.isFinite(priorityRaw)) return priorityRaw;
  return listIndex + 1;
}

/**
 * Prefers `products[]`; merges legacy `productMappings[]` without overwriting keys.
 * @param {object} quiz
 * @returns {Map<string, { title: string, handle: string, priority: number }>}
 */
function normalizeProductsFromQuiz(quiz) {
  const map = new Map();

  if (Array.isArray(quiz?.products)) {
    for (let index = 0; index < quiz.products.length; index++) {
      const item = quiz.products[index];
      const key = toSafeId(item?.key ?? "");
      if (!key) continue;

      map.set(key, {
        title: toText(item?.product?.title ?? item?.title ?? ""),
        handle: toText(item?.product?.handle ?? item?.handle ?? ""),
        priority: resolveProductCatalogPriority(item, index),
      });
    }
  }

  if (Array.isArray(quiz?.productMappings)) {
    for (let index = 0; index < quiz.productMappings.length; index++) {
      const item = quiz.productMappings[index];
      const key = toSafeId(item?.key ?? "");
      if (!key || map.has(key)) continue;

      map.set(key, {
        title: toText(item?.product?.title ?? item?.title ?? ""),
        handle: toText(item?.product?.handle ?? item?.handle ?? ""),
        priority: resolveProductCatalogPriority(item, index),
      });
    }
  }

  return map;
}

function normalizeTabItems(tab) {
  const items = Array.isArray(tab?.items)
    ? tab.items
    : Array.isArray(tab?.steps)
      ? tab.steps
      : Array.isArray(tab?.products)
        ? tab.products
        : [];

  return items
    .map((item) => toText(item?.label ?? item?.title ?? item?.name ?? item))
    .filter(Boolean);
}

function toText(value) {
  return typeof value === "string" ? value.trim() : "";
}

function toSafeId(value) {
  const text = toText(String(value ?? ""));
  if (!text) return "";

  return text.toLowerCase().replace(/[^a-z0-9-_]+/g, "-");
}

function uniqueValues(values) {
  return [...new Set(values)];
}

/**
 * @typedef {Object} ResultProfile
 * @property {string} id
 * @property {string} title
 * @property {string} description
 * @property {{ src: string, alt: string }} summaryImage
 */

/**
 * @typedef {Object} RoutineDisplayItem
 * @property {string} productKey
 * @property {string} label
 * @property {string} [stepType]
 */

/**
 * @typedef {Object} NormalizedRuleAction
 * @property {string} type
 */

/**
 * Plain snapshot of a normalized rule for console / debugging (JSON-friendly).
 * @param {object | null | undefined} rule
 */
function snapshotNormalizedRule(rule) {
  if (!rule || typeof rule !== "object") {
    return {
      id: "",
      priority: 999,
      conditions: [],
      actions: [],
    };
  }

  const pr = Number(rule.priority);
  const snap = {
    id: toText(rule.id ?? ""),
    priority: Number.isFinite(pr) ? pr : 999,
    conditions: Array.isArray(rule.conditions)
      ? rule.conditions.map((c) => ({
          questionId: toText(c?.questionId ?? ""),
          answerId: toText(c?.answerId ?? ""),
        }))
      : [],
    actions: Array.isArray(rule.actions)
      ? rule.actions.map((a) =>
          a && typeof a === "object" ? { ...a } : {},
        )
      : [],
  };
  if (rule.isFallback === true) {
    snap.isFallback = true;
  }
  return snap;
}

// --------------------------------------------------
// BUSINESS RULE — Hero product deduplication (result tabs)
// --------------------------------------------------
// WHY this exists:
// The quiz can match multiple result rules and inject several products with
// stepType "hero". Hero products are secondary, concern-driven recommendations
// (e.g. a targeted mask or balm). Normal routine tabs (AM/PM/etc.) are the
// primary routine. When the same productKey appears in both places, the normal
// routine version always wins — merchants and clients should not see duplicate
// cards on the hero tab or lose the unique hero in favor of a duplicate.
//
// Tab model (fully dynamic — never hardcode tab ids such as am-routine,
// pm-routine, or hero-product):
// - quiz.result.tabs defines every result tab.
// - Most tabs own their own product list after rules run.
// - Exactly one tab may set showAllProducts: true (the aggregated "all products"
//   view). That tab is rebuilt in JS; it does not come from rules directly.
//
// Per-tab cleanup — dedupeHeroProductsInRealRoutineTabs():
// - On each real (non-aggregated) tab, remove hero rows whose productKey already
//   exists as a non-hero item on any real tab globally.
// - Then keep at most one valid hero per tab (first surviving hero in tab order).
//
// Aggregated tab — buildAggregatedRoutineItems():
// - Unique productKey values only (no duplicates across tabs).
// - Unlimited normal (non-hero) routine products, in quiz.result.tabs order.
// - At most ONE hero product globally for the aggregated list.
// - Invalid heroes (already in routines, or duplicate hero keys) are filtered
//   BEFORE the single-hero limit is applied, so a duplicate hero must never
//   consume the only allowed hero slot (e.g. Serum+Mask → Serum + Mask, not Serum only).
//
// Detection: hero vs routine is stepType === "hero" (see isHeroRoutineStep).
//
// Where to change behavior:
// - Helpers in this block only.
// - Call sites at the end of computeQuizResultState() (search: aggregatedTab).
// Do not scatter hero dedupe logic in rendering or cart code.
// --------------------------------------------------
// DYNAMIC HERO PRODUCT DEDUPLICATION (implementation)
// --------------------------------------------------

/** @param {string} [stepType] */
function isHeroRoutineStep(stepType) {
  return toText(stepType ?? "").toLowerCase() === "hero";
}

/**
 * Tab that merges every unique product from non-aggregated routine tabs.
 * @param {object[]} tabs
 * @returns {object | null}
 */
function findAggregatedResultTab(tabs) {
  return tabs.find((t) => t?.showAllProducts === true) ?? null;
}

/**
 * Routine tabs that own their own product lists (everything except the aggregated tab).
 * @param {object[]} tabs
 * @param {string} aggregatedTabId
 * @returns {string[]}
 */
function getRealRoutineTabIds(tabs, aggregatedTabId) {
  return tabs
    .filter(
      (t) =>
        t?.id &&
        t.id !== aggregatedTabId &&
        t?.showAllProducts !== true,
    )
    .map((t) => t.id);
}

/**
 * Per real routine tab: drop hero rows that duplicate non-hero routines globally,
 * then keep at most one valid hero (e.g. when several hero rules match).
 * @param {string[]} realTabIds
 * @param {Map<string, RoutineDisplayItem[]>} routineItemsByTab
 */
function dedupeHeroProductsInRealRoutineTabs(
  realTabIds,
  routineItemsByTab,
) {
  // Pass 1: global set of primary routine keys (any tab, any non-hero step).
  /** @type {Set<string>} */
  const nonHeroProductKeys = new Set();

  for (const tabId of realTabIds) {
    for (const item of routineItemsByTab.get(tabId) ?? []) {
      if (isHeroRoutineStep(item.stepType)) continue;
      const productKey = toText(item?.productKey ?? "");
      if (productKey) nonHeroProductKeys.add(productKey);
    }
  }

  // Pass 2: rewrite each tab — non-hero rows unchanged; heroes filtered then capped.
  for (const tabId of realTabIds) {
    const items = routineItemsByTab.get(tabId);
    if (!items?.length) continue;

    let heroKept = false;
    /** @type {RoutineDisplayItem[]} */
    const next = [];

    for (const item of items) {
      if (!isHeroRoutineStep(item.stepType)) {
        next.push(item);
        continue;
      }

      const productKey = toText(item?.productKey ?? "");
      // Drop hero duplicates of normal routine products (routine wins).
      if (!productKey || nonHeroProductKeys.has(productKey)) continue;
      // First valid hero only per tab (multiple hero rules may have matched).
      if (heroKept) continue;

      heroKept = true;
      next.push(item);
    }

    routineItemsByTab.set(tabId, next);
  }
}

/**
 * Display order for showAllProducts tab (lower priority value = earlier in list).
 * @param {string} productKey
 * @param {Map<string, { priority?: number }>} productMap
 * @returns {number}
 */
function getProductDisplayPriority(productKey, productMap) {
  const mapping = productMap?.get(productKey);
  const raw = Number(mapping?.priority);
  return Number.isFinite(raw) ? raw : 999;
}

/**
 * Sort aggregated rows by admin product catalog priority (after dedupe/hero logic).
 * @param {RoutineDisplayItem[]} items
 * @param {Map<string, { priority?: number }>} productMap
 */
function sortAggregatedRoutineItemsByProductPriority(items, productMap) {
  items.sort((a, b) => {
    return (
      getProductDisplayPriority(a.productKey, productMap) -
      getProductDisplayPriority(b.productKey, productMap)
    );
  });
}

/**
 * Builds the aggregated tab in three phases (see block comment above):
 * 1) routine products (non-hero, tab order, deduped)
 * 2) filter invalid heroes (already in routine, duplicate hero keys)
 * 3) append at most one surviving hero
 * 4) sort by catalog priority (display only)
 * @param {object[]} tabs
 * @param {Map<string, RoutineDisplayItem[]>} routineItemsByTab
 * @param {string} aggregatedTabId
 * @param {Map<string, { priority?: number }>} productMap
 * @returns {RoutineDisplayItem[]}
 */
function buildAggregatedRoutineItems(
  tabs,
  routineItemsByTab,
  aggregatedTabId,
  productMap,
) {
  const realTabIds = getRealRoutineTabIds(tabs, aggregatedTabId);

  /** @type {RoutineDisplayItem[]} */
  const aggregated = [];
  const seenProductKeys = new Set();
  /** @type {Set<string>} Keys from primary routine steps; used to reject duplicate heroes. */
  const routineProductKeys = new Set();

  // Phase 1: all non-hero products, tab order, globally unique (no hero slot used).
  for (const tabId of realTabIds) {
    for (const item of routineItemsByTab.get(tabId) ?? []) {
      if (isHeroRoutineStep(item.stepType)) continue;

      const productKey = toText(item?.productKey ?? "");
      if (!productKey || seenProductKeys.has(productKey)) continue;

      seenProductKeys.add(productKey);
      routineProductKeys.add(productKey);

      aggregated.push({
        productKey,
        label: item.label,
        stepType: item.stepType ?? "",
      });
    }
  }

  // Phase 2: collect heroes only; filter before max-one so invalid rows never win the slot.
  /** @type {RoutineDisplayItem[]} */
  const validHeroCandidates = [];
  const seenHeroKeys = new Set();

  for (const tabId of realTabIds) {
    for (const item of routineItemsByTab.get(tabId) ?? []) {
      if (!isHeroRoutineStep(item.stepType)) continue;

      const productKey = toText(item?.productKey ?? "");
      if (!productKey) continue;
      // Hero already covered by a normal routine step on any tab.
      if (routineProductKeys.has(productKey)) continue;
      // Duplicate hero keys across tabs / rules.
      if (seenHeroKeys.has(productKey)) continue;

      seenHeroKeys.add(productKey);
      validHeroCandidates.push({
        productKey,
        label: item.label,
        stepType: item.stepType ?? "",
      });
    }
  }

  // Phase 3: append first valid hero only (candidates list is already filtered).
  const heroToInclude = validHeroCandidates[0];
  if (heroToInclude && !seenProductKeys.has(heroToInclude.productKey)) {
    seenProductKeys.add(heroToInclude.productKey);
    aggregated.push(heroToInclude);
  }

  // Phase 4: display order from admin product list priority (does not change which products are included).
  sortAggregatedRoutineItemsByProductPriority(aggregated, productMap);

  return aggregated;
}

/**
 * Evaluates rules in priority order and resolves profiles, routine templates, and product labels.
 * @param {object} quiz — normalized quiz (see normalizeQuiz)
 * @param {Record<string, string[]>} answers
 * @returns {{ activeProfile: ResultProfile | null, routineItemsByTab: Map<string, RoutineDisplayItem[]>, appliedRuleIds: string[], appliedRules: ReturnType<typeof snapshotNormalizedRule>[] }}
 */
function computeQuizResultState(quiz, answers) {
  const routineItemsByTab = new Map();
  /** @type {ResultProfile | null} */
  let activeProfile = null;
  /** @type {string[]} */
  const appliedRuleIds = [];
  /** @type {ReturnType<typeof snapshotNormalizedRule>[]} */
  const appliedRules = [];

  const productMap = quiz?.products instanceof Map ? quiz.products : new Map();
  const profilesById = new Map();

  for (const profile of quiz?.resultProfiles ?? []) {
    if (profile?.id) {
      profilesById.set(profile.id, profile);
    }
  }

  const templatesById = new Map();
  for (const template of quiz?.routineTemplates ?? []) {
    if (template?.id) {
      templatesById.set(template.id, template);
    }
  }

  /**
   * @param {string} routineId
   * @param {string} productKey
   * @param {string} [stepType]
   */
  const appendProductToRoutine = (routineId, productKey, stepType) => {
    if (!routineId || !productKey) return;

    const mapping = productMap.get(productKey);
    const label = mapping?.title ? mapping.title : productKey;

    const list = routineItemsByTab.get(routineId) ?? [];
    if (list.some((entry) => entry.productKey === productKey)) return;

    list.push({
      productKey,
      label,
      stepType: toText(stepType ?? ""),
    });
    routineItemsByTab.set(routineId, list);
  };

  const rules = Array.isArray(quiz?.result?.rules)
    ? [...quiz.result.rules]
    : [];
  rules.sort((a, b) => {
    const pa = Number.isFinite(Number(a?.priority)) ? Number(a.priority) : 999;
    const pb = Number.isFinite(Number(b?.priority)) ? Number(b.priority) : 999;
    return pa - pb;
  });

  const applyRuleActions = (rule) => {
    for (const action of rule.actions ?? []) {
      if (action.type === "SET_PROFILE") {
        const profile = profilesById.get(action.profileId);
        if (!profile) {
          skinQuizDevWarn(
            "profile-miss",
            "SET_PROFILE references unknown profileId (normalized).",
            action.profileId,
          );
        }
        activeProfile = profile ?? null;
        continue;
      }

      if (action.type === "ADD_ROUTINE_TEMPLATE") {
        const template = templatesById.get(action.templateId);
        if (!template) {
          skinQuizDevWarn(
            "template-miss",
            "ADD_ROUTINE_TEMPLATE references unknown templateId (normalized).",
            action.templateId,
          );
          continue;
        }

        for (const step of template.steps ?? []) {
          appendProductToRoutine(template.routine, step.productKey, step.type);
        }
        continue;
      }

      if (action.type === "ADD_ROUTINE_STEP") {
        appendProductToRoutine(
          action.routine,
          action.productKey,
          action.stepType,
        );
      }
    }
  };

  const runMatchedRule = (rule) => {
    const snap = snapshotNormalizedRule(rule);
    appliedRules.push(snap);
    if (snap.id) {
      appliedRuleIds.push(snap.id);
    }
    applyRuleActions(rule);
  };

  let anyConditionalRuleMatched = false;

  for (const rule of rules) {
    const conditions = Array.isArray(rule?.conditions) ? rule.conditions : [];
    if (conditions.length === 0) continue;

    const matches = conditions.every((condition) => {
      const selected = answers[condition.questionId] ?? [];
      return Array.isArray(selected) && selected.includes(condition.answerId);
    });

    if (!matches) continue;

    anyConditionalRuleMatched = true;
    runMatchedRule(rule);
  }

  if (!anyConditionalRuleMatched) {
    for (const rule of rules) {
      const conditions = Array.isArray(rule?.conditions) ? rule.conditions : [];
      if (conditions.length > 0) continue;

      runMatchedRule(rule);
      break;
    }
  }

  const tabs = Array.isArray(quiz?.result?.tabs) ? quiz.result.tabs : [];
  // Hero deduplication call sites — see BUSINESS RULE block above this section.
  const aggregatedTab = findAggregatedResultTab(tabs);
  if (aggregatedTab?.id) {
    const realTabIds = getRealRoutineTabIds(tabs, aggregatedTab.id);
    dedupeHeroProductsInRealRoutineTabs(realTabIds, routineItemsByTab);
    routineItemsByTab.set(
      aggregatedTab.id,
      buildAggregatedRoutineItems(
        tabs,
        routineItemsByTab,
        aggregatedTab.id,
        productMap,
      ),
    );
  }

  return { activeProfile, routineItemsByTab, appliedRuleIds, appliedRules };
}

/**
 * Omits empty strings so line properties stay clean in cart / Flow.
 * @param {Record<string, string> | undefined} props
 * @returns {Record<string, string> | undefined}
 */
function compactLineProperties(props) {
  if (!props || typeof props !== "object") return undefined;
  /** @type {Record<string, string>} */
  const out = {};
  for (const [k, v] of Object.entries(props)) {
    if (v == null) continue;
    const s = String(v).trim();
    if (s === "") continue;
    out[k] = s;
  }
  return Object.keys(out).length ? out : undefined;
}

/**
 * @returns {string}
 */
function getCartAddSectionsQuery() {
  const cartItemsComponents = document.querySelectorAll("cart-items-component");
  /** @type {string[]} */
  const sectionIds = [];
  for (const el of cartItemsComponents) {
    if (el instanceof HTMLElement && el.dataset.sectionId) {
      sectionIds.push(el.dataset.sectionId);
    }
  }
  return sectionIds.join(",");
}

/**
 * Adds all line items in a **single** POST /cart/add.json request (Shopify bulk API).
 * Falls back to sequential adds only when the bulk call itself is rejected by Shopify
 * (e.g. one invalid variant causes the whole batch to fail). Mirrors the same return
 * shape as the old sequential helper so the caller needs no changes.
 *
 * @param {{ variantId: string, quantity: number, properties?: Record<string, string> }[]} items
 * @param {unknown[]} [metaForDebug] parallel rows for debug logging (may include productKey / handle)
 * @returns {Promise<{ addedCount: number, failures: { item: any, data: any }[], lastSuccessData: any | null }>}
 */
async function postCartLineItemsBulk(items, metaForDebug) {
  if (typeof Theme === "undefined" || !Theme.routes?.cart_add_url) {
    return {
      addedCount: 0,
      failures: items.map((item, i) => ({
        item: metaForDebug?.[i] ?? item,
        data: {
          status: 400,
          message: "Cart unavailable",
          description: "",
          errors: {},
        },
      })),
      lastSuccessData: null,
    };
  }

  const sections = getCartAddSectionsQuery();

  /** Build the lines array for the bulk request. */
  /** @type {{ id: number, quantity: number, properties?: Record<string, string> }[]} */
  const lines = [];
  /** @type {{ item: any, data: any }[]} */
  const preValidationFailures = [];

  for (let i = 0; i < items.length; i++) {
    const item = items[i];
    const dbg = metaForDebug?.[i] ?? item;
    const idNum = Number(item.variantId);
    if (!Number.isFinite(idNum) || idNum <= 0) {
      preValidationFailures.push({
        item: dbg,
        data: {
          status: 400,
          message: "Invalid variant",
          description: String(item.variantId ?? ""),
          errors: {},
        },
      });
      skinQuizDebug("cart-add-skip-bad-id", { item: dbg });
      continue;
    }
    const line = /** @type {{ id: number, quantity: number, properties?: Record<string, string> }} */ ({
      id: idNum,
      quantity: item.quantity,
    });
    if (item.properties && Object.keys(item.properties).length > 0) {
      line.properties = item.properties;
    }
    lines.push(line);
  }

  if (lines.length === 0) {
    return { addedCount: 0, failures: preValidationFailures, lastSuccessData: null };
  }

  const payload = { items: lines, sections };
  skinQuizDebug("cart-add-bulk-request", { lineCount: lines.length, payload });

  /** @type {string} */
  let rawText;
  try {
    const response = await fetch(Theme.routes.cart_add_url, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Accept: "application/json",
      },
      body: JSON.stringify(payload),
    });
    rawText = await response.text();
  } catch (err) {
    skinQuizDebug("cart-add-bulk-network-error", { err });
    // Network failure: nothing was added, report all as failures.
    return {
      addedCount: 0,
      failures: [
        ...preValidationFailures,
        ...lines.map((line, i) => ({
          item: metaForDebug?.[i] ?? items[i],
          data: {
            status: 0,
            message: "Network error",
            description: err instanceof Error ? err.message : String(err),
            errors: {},
          },
        })),
      ],
      lastSuccessData: null,
    };
  }

  /** @type {any} */
  let data;
  try {
    data = JSON.parse(rawText);
  } catch {
    skinQuizDebug("cart-add-bulk-json-parse-error", { snippet: rawText.slice(0, 240) });
    // Unparseable response — treat as total failure.
    const failureEntry = {
      status: 422,
      message: "Invalid cart response",
      description: rawText.slice(0, 240),
      errors: {},
    };
    return {
      addedCount: 0,
      failures: [
        ...preValidationFailures,
        ...lines.map((line, i) => ({ item: metaForDebug?.[i] ?? items[i], data: failureEntry })),
      ],
      lastSuccessData: null,
    };
  }

  // Shopify returns { status: <number>, message: "..." } on error.
  if (data.status) {
    skinQuizDebug("cart-add-bulk-failed", { data });
    // The bulk call was rejected (e.g. one invalid variant). Fall back to sequential
    // so any valid items still get added and per-item error details are surfaced.
    skinQuizDebug("cart-add-bulk-fallback-sequential", { lineCount: lines.length });
    const sequentialResult = await postCartLineItemsSequential(items, metaForDebug);
    return {
      addedCount: sequentialResult.addedCount,
      failures: [...preValidationFailures, ...sequentialResult.failures],
      lastSuccessData: sequentialResult.lastSuccessData,
    };
  }

  // Success: all items were added in one shot.
  const addedCount = lines.reduce((sum, l) => sum + l.quantity, 0);
  skinQuizDebug("cart-add-bulk-ok", { addedCount, itemCount: lines.length });
  return {
    addedCount,
    failures: preValidationFailures,
    lastSuccessData: data,
  };
}

/**
 * Sequential fallback: adds one line per request so partial failures preserve items that succeeded.
 * Called automatically by postCartLineItemsBulk when the bulk request is rejected by Shopify.
 * Mirrors `ProductFormComponent.#processBatchAddToCart` payload shape (JSON + sections).
 *
 * @param {{ variantId: string, quantity: number, properties?: Record<string, string> }[]} items
 * @param {unknown[]} [metaForDebug] parallel rows for debug logging (may include productKey / handle)
 * @returns {Promise<{ addedCount: number, failures: { item: any, data: any }[], lastSuccessData: any | null }>}
 */
async function postCartLineItemsSequential(items, metaForDebug) {
  if (typeof Theme === "undefined" || !Theme.routes?.cart_add_url) {
    return {
      addedCount: 0,
      failures: items.map((item, i) => ({
        item: metaForDebug?.[i] ?? item,
        data: {
          status: 400,
          message: "Cart unavailable",
          description: "",
          errors: {},
        },
      })),
      lastSuccessData: null,
    };
  }

  const sections = getCartAddSectionsQuery();
  let addedCount = 0;
  /** @type {any | null} */
  let lastSuccessData = null;
  /** @type {{ item: any, data: any }[]} */
  const failures = [];

  for (let i = 0; i < items.length; i++) {
    const item = items[i];
    const dbg = metaForDebug?.[i] ?? item;
    const idNum = Number(item.variantId);
    if (!Number.isFinite(idNum) || idNum <= 0) {
      failures.push({
        item: dbg,
        data: {
          status: 400,
          message: "Invalid variant",
          description: String(item.variantId ?? ""),
          errors: {},
        },
      });
      skinQuizDebug("cart-add-skip-bad-id", { item: dbg });
      continue;
    }

    const line = /** @type {{ id: number, quantity: number, properties?: Record<string, string> }} */ ({
      id: idNum,
      quantity: item.quantity,
    });
    if (item.properties && Object.keys(item.properties).length > 0) {
      line.properties = item.properties;
    }

    const payload = { items: [line], sections };

    skinQuizDebug("cart-add-request-item", { index: i, payload, meta: dbg });

    /** @type {string} */
    let rawText;
    try {
      const response = await fetch(Theme.routes.cart_add_url, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Accept: "application/json",
        },
        body: JSON.stringify(payload),
      });
      rawText = await response.text();
    } catch (err) {
      failures.push({
        item: dbg,
        data: {
          status: 0,
          message: "Network error",
          description: err instanceof Error ? err.message : String(err),
          errors: {},
        },
      });
      skinQuizDebug("cart-add-network-error", { item: dbg, err });
      continue;
    }

    /** @type {any} */
    let data;
    try {
      data = JSON.parse(rawText);
    } catch {
      failures.push({
        item: dbg,
        data: {
          status: 422,
          message: "Invalid cart response",
          description: rawText.slice(0, 240),
          errors: {},
        },
      });
      skinQuizDebug("cart-add-json-parse-error", {
        snippet: rawText.slice(0, 240),
      });
      continue;
    }

    if (data.status) {
      failures.push({ item: dbg, data });
      skinQuizDebug("cart-add-item-failed", { item: dbg, data });
      continue;
    }

    lastSuccessData = data;
    addedCount += item.quantity;
    skinQuizDebug("cart-add-item-ok", { index: i, variantId: item.variantId });
  }

  return { addedCount, failures, lastSuccessData };
}

/**
 * @param {string} endpoint
 * @param {Record<string, unknown>} payload
 */
async function postJson(endpoint, payload) {
  const response = await fetch(endpoint, {
    method: "POST",
    credentials: "same-origin",
    headers: {
      "Content-Type": "application/json",
      Accept: "application/json",
    },
    body: JSON.stringify(payload),
  });

  let data = null;
  try {
    data = await response.json();
  } catch {
    data = null;
  }

  return { ok: response.ok, status: response.status, data };
}

function getSkinQuizSubmitEndpoint(component) {
  if (component instanceof HTMLElement) {
    const datasetUrl = component.dataset.submitUrl?.trim();
    if (datasetUrl) return datasetUrl;
  }

  const fallback = document.querySelector("skin-quiz-component");
  if (fallback instanceof HTMLElement) {
    const datasetUrl = fallback.dataset.submitUrl?.trim();
    if (datasetUrl) return datasetUrl;
  }

  if (typeof Theme !== "undefined" && Theme.appRoutes?.submitQuiz) {
    return Theme.appRoutes.submitQuiz;
  }
  return "/apps/maiwe-core/submit-quiz";
}

/**
 * @param {{ email: string, quizId: string, answers: Record<string, string[]>, result: Record<string, unknown> }} payload
 * @param {HTMLElement} [component]
 */
async function submitSkinQuizSubmission(payload, component) {
  if (SKIN_QUIZ_MOCK_SUBMISSION_API) {
    console.debug("[skin-quiz] mock submission (API skipped)", payload);
    return {
      submission: {
        submissionId: `mock-${Date.now()}`,
        submittedAt: new Date().toISOString(),
      },
    };
  }

  const endpoint = getSkinQuizSubmitEndpoint(component);
  const response = await postJson(endpoint, payload);

  if (!response.ok) {
    const body =
      response.data && typeof response.data === "object" ? response.data : null;
    const serverMsg =
      body && typeof body.error === "string" && body.error.trim()
        ? body.error.trim()
        : null;
    const err = new Error(
      serverMsg ||
        `We couldn't save your quiz (error ${response.status}). Please try again.`,
    );
    throw err;
  }

  if (!response.data || typeof response.data !== "object") {
    throw new Error("Submit quiz response is invalid");
  }

  return response.data;
}

function cssEscape(value) {
  if (window.CSS?.escape) return window.CSS.escape(value);

  return String(value).replace(/["\\]/g, "\\$&");
}
